import { ITEMS, bagOf, HOTBAR, BAG_SLOTS, wieldedWeapon, stackCaption, stackWear, gearOf } from './sim/inventory.ts';
import { tempWord } from './sim/fields.ts';
import { activeNames } from './sim/afflictions.ts';

import { BARE_IN_WILD, drawWeapon, thornVariant } from './art.ts';
import { mixHex } from './grass.ts';
/* EVERYTHING THAT ONLY DRAWS.
 *
 * The second cut out of `src/game.ts`, after `src/audio.ts`. An adversarial
 * reviewer scored `structure` 4 out of 10 on a 4,535-line class that owned
 * area loading, collision, the rescue search, saves, the shop, spawning,
 * puzzles, audio AND eleven drawing methods — and made the case with a defect
 * rather than an opinion: two economy guards whose write and read sat eleven
 * hundred lines apart had no test between them, and both survived the whole
 * 769-check suite as live exploits.
 *
 * This file is the part of that class with no opinion about the simulation.
 * Nothing in here mutates game state; it reads it and paints. `Game` keeps a
 * one-line delegator for each, so `this.drawHud()` still reads the same at
 * every call site and in the suite.
 *
 * NB: several soak checks read the SOURCE of these functions as text — the
 * key legend, the shout audit, the sub-pixel fill audit. Moving code out of
 * `game.js` broke three of them, every one loudly, because each carries a
 * tripwire that fails when its parse finds nothing. If you move a drawing
 * method again, expect to repoint a check, and keep those tripwires.
 */
import {
  T, TS, UI, BIOMES, EDGED, VARIANTS, VARIED, animOf, tile, sprite, particle,
  drawGlim, drawLeaf, drawPod, drawSeed, drawSeedBig, drawSigil, drawText, GLYPH_ADVANCE,
  textWidth as glyphWidth,
} from './art.ts';
import { PW, PH, overlaps } from './entities.ts';
import * as M3 from './model3d.ts';
import {
  COLS, ROWS, WORLD_W, WORLD_H, CHARM_MARK, linked,
} from './world.ts';
import { drawGlyph, glyphFor as glyphOf, syllables as syllablesOf, bearingWords, WORDS,
  drawWord, drawWordDown }
  from './deepmark.ts';
import { actionLabel } from './labels.ts';
import { RNG, mix } from './rng.ts';
import { VW, VH, HUD_H, ROMAN, VIGOR_PER_ROW, clock, BAG_BOX_Y, bagPitch, bagButtons, loadoutBoxes, HOTBAR_X, HOTBAR_Y, HOTBAR_PITCH } from './view.ts';
import { slab, centered, smallText, MATERIAL } from './ui-craft.ts';
import { EMBER_COOL } from './rules.ts';
import * as A from './audio.ts';

/* ONE COORDINATE SPACE, WHICH THIS DID NOT HAVE (round 54, found by codex
 * reading for the shape that produced the frost bug an hour earlier).
 *
 * The gloom is painted once over the WHOLE WINDOW, and it was handed three
 * different spaces at once: the player at WORLD coordinates, thrown flames at
 * WORLD coordinates, and braziers at SCREEN-LOCAL ones read from `game.tiles`,
 * which is the player's own screen. So the lantern's hole was drawn hundreds of
 * pixels off the glass -- MEASURED: with the player at 128,96 on the window, the
 * mask let 119 through where they stood against 248 on average, so the one place
 * that should have been brightest was DARKER than the average of the picture --
 * and only the player's screen had braziers, punched at the wrong offsets.
 * Crossing a seam changed which braziers existed and where their holes fell.
 *
 * Window coordinates for all three now, which is `world - cam`, the same
 * arithmetic `visibleScreens` uses for the offsets the world is drawn at. */
export function drawGloom(game, g) {
  const cam = game.camera();
  const lights = [[game.player.cx - cam.x, game.player.cy - cam.y, 3.4]];
  for (const [sx, sy, ox, oy] of game.visibleScreens(cam)) {
    const here = sx === game.area.x && sy === game.area.y;
    const baked = game.bakedParts ? game.bakedParts(sx, sy) : null;
    const tiles = here ? game.tiles : (baked && baked.tiles ? baked.tiles : null);
    if (!tiles) continue;   // a screen not baked yet has no braziers to find
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        if (tiles[y][x] === T.BRAZIER_LIT) lights.push([ox + x * TS + 8, oy + y * TS + 8, 3.0]);
      }
    }
  }
  // a thrown flame carries its own light
  for (const sh of game.shots) {
    if (sh.friendly && sh.kind === 'arc' && game.state.hasEmber) lights.push([sh.cx - cam.x, sh.cy - cam.y, 2.2]);
  }
  // ONE FLICKER TERM FOR THE WHOLE MASK moved every shadowed tile on the
  // screen in lockstep — measured at up to 45.9% of the mask changing in a
  // single frame, which is a sheet being pulled about rather than lamplight.
  // Each light breathes on its own period, phased off its own position, so
  // the player's lantern and a brazier across the room are never in step.
  const lit = lights.map(([lx, ly, r], i) => {
    const phase = (lx * 0.37 + ly * 0.61 + i * 2.1);
    const period = 9 + ((Math.round(lx) * 7 + Math.round(ly) * 3 + i * 5) % 7);
    return [lx, ly, r + 0.22 * Math.sin(game.frame / period + phase)];
  });
  // ...AND THE MASK IS FINER THAN THE THING IT IS SHADOWING. A 16px cell
  // over 1px art is a checkerboard: 156 of 192 cells carrying one of 29
  // alpha levels, adjacent bands 0.17 apart, so the light pool read as a
  // lumpy grey plus-sign rather than a lantern. Half-tile cells and FIVE
  // deliberate bands: the step is still hard, which is the intent in a game
  // of hard pixels, but it is now a step you can see the shape of.
  /* IN THE INK LOOK the dark is a wash, not a mask in steps (2026-09-28): a deep blue ink laid over the world with a soft
     round clearing at every light, on its own canvas so the clearings take only the wash away */
  if (game.inkUiOn) {
    let cv = (drawGloom as any).cv as HTMLCanvasElement; if (!cv) { cv = (drawGloom as any).cv = document.createElement('canvas'); cv.width = PW; cv.height = PH; }
    const gg = cv.getContext('2d')!; gg.globalCompositeOperation = 'source-over'; gg.clearRect(0, 0, PW, PH); gg.fillStyle = 'rgba(44,42,56,0.7)'; gg.fillRect(0, 0, PW, PH);
    gg.globalCompositeOperation = 'destination-out';
    for (const [lx, ly, r] of lit) { const R = r * TS * 1.15, gr = gg.createRadialGradient(lx, ly, R * 0.25, lx, ly, R); gr.addColorStop(0, 'rgba(0,0,0,1)'); gr.addColorStop(0.55, 'rgba(0,0,0,0.75)'); gr.addColorStop(1, 'rgba(0,0,0,0)'); gg.fillStyle = gr; gg.beginPath(); gg.arc(lx, ly, R, 0, Math.PI * 2); gg.fill(); }
    gg.globalCompositeOperation = 'source-over'; g.drawImage(cv, 0, 0); return;
  }
  const CELL = 8, BANDS = 5, DARKEST = 0.88;
  for (let y = 0; y < PH; y += CELL) {
    for (let x = 0; x < PW; x += CELL) {
      const cx = x + CELL / 2, cy = y + CELL / 2;
      let near = Infinity;
      for (const [lx, ly, r] of lit) {
        near = Math.min(near, Math.hypot(cx - lx, cy - ly) / TS - r);
      }
      if (near <= 0) continue;
      const band = Math.min(BANDS, Math.ceil(near * 1.7));
      const a = (band / BANDS) * DARKEST;
      g.fillStyle = 'rgba(9,7,14,' + a.toFixed(3) + ')';
      g.fillRect(x, y, CELL, CELL);
    }
  }
}

/* `pal` may be a FUNCTION, and that is how a biome boundary stops being a
 * screen boundary. Given one, it is asked per tile for the palette and the
 * cache key to draw that tile with — the key is still one of the six biome
 * names, so the baked-art cache keeps working and the cost is a lookup. */
export function drawTiles(game, tiles, pal, palKey, ox, oy, coords, only = null) {
  const perTile = typeof pal === 'function';
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      if (only && !(only instanceof Set ? only.has(tiles[y][x]) : only(tiles[y][x], x, y))) continue;   // coordinate filters retain real neighbours for a single prop
      if (perTile) {
        const p = pal(x, y);
        drawOneTile(game, tiles, p.pal, p.key, x, y, ox, oy, coords);
      } else drawOneTile(game, tiles, pal, palKey, x, y, ox, oy, coords);
    }
  }
}

/* --- WHAT A STANDING STONE CAN HOLD.
 *
 * The pale band `art.ts` paints on a STONE tile is columns 5..11 — SEVEN
 * pixels — over the head's rows 4..15 and the foot's rows 0..11. Every number
 * below is read off that band, and none of them is a matter of taste:
 *
 *   x0        a 7px figure is 6 columns wide, so column 11 is left as the
 *             margin against the dark edge at column 12.
 *   size      SEVEN, which is the floor. Rasterised through `drawGlyph` and
 *             differenced, the eleven corpus marks sit 7 pixels apart at 7px,
 *             4 apart at 6px and ONE apart at 5px (`ra`/`ro` differ by a
 *             single pixel). Four of ten is the ratio `deepmark.ts` already
 *             threw the old stride out for. Anything smaller buys a fourth
 *             mark by making the other three unreadable.
 *   gap       one blank row, so the stems break. Stacked flush they merge
 *             into a single ladder and the mark boundaries vanish.
 *   rows*     24 continuous rows head-to-foot, 12 on a one-tile stone.
 *
 * Exported as ONE object rather than copied into the harness: `test/water.ts`
 * has to know where on the tile the ink is allowed to land before it can say
 * that none of it landed elsewhere, and a second set of these numbers in the
 * test would agree with a broken layout as readily as with a working one.
 */
export const STONE_FACE = {
  /** first and last column of the pale band, in tile pixels */
  x0: 5, x1: 11,
  /** the head's first face row, the foot's last, and where a one-tile stone
   *  starts its single mark */
  headTop: 4, footBottom: 11, shortTop: 2,
  size: 7, gap: 1, rowsTall: 24, rowsShort: 12,
};
const FACE_X = STONE_FACE.x0, FACE_SIZE = STONE_FACE.size, FACE_GAP = STONE_FACE.gap;
const FACE_ROWS_TALL = STONE_FACE.rowsTall, FACE_ROWS_SHORT = STONE_FACE.rowsShort;

/** WHICH SYLLABLES A STONE THAT TALL CUTS, and in reading order.
 *
 *  A stone is not always tall enough for the sentence it carries, so
 *  something has to give, and what gives is chosen rather than truncated:
 *
 *    the bearing's head mark   the one fact the world does not otherwise
 *                              state — which way the beast's place lies
 *    the noun's head mark      which of the two sentences this is: a hollow
 *                              (`su`, `ko`, `me`: the beast the stone names)
 *    the bearing's tail mark   completes the word
 *    the noun's tail mark      completes the word
 *
 *  A one-word stone — the hollow is on this very screen, so there is no
 *  bearing to give — falls back to its own two syllables, which is where `ko`
 *  comes from and the only place in the world it is cut.
 *
 *  Exported because `test/water.ts` asks what a stone should say before it
 *  reads what it does say, and a second spelling of this rule in the harness
 *  would be a second model of the inscription. */
export function faceMarks(said, rows) {
  const cap = Math.floor((rows + FACE_GAP) / (FACE_SIZE + FACE_GAP));
  const words = said.map((w) => syllablesOf(w));
  const rank = words.length > 1
    ? [[1, 0], [0, 0], [1, 1], [0, 1]] : [[0, 0], [0, 1]];
  return rank.filter(([w, s]) => words[w] && words[w][s] !== undefined)
    .slice(0, Math.max(0, cap))
    .sort((a, b) => a[0] - b[0] || a[1] - b[1])
    .map(([w, s]) => words[w][s]);
}

/** ONE TILE, THE WAY THE GRID DRAWS IT.
 *
 * Split out of `drawTiles` rather than copied, because `Game.drawMade` needs
 * to draw a handful of tiles again over the canopy and a second spelling of
 * "how a tile is drawn" — variants, edge masks, head-or-foot, animation phase
 * and the inscription — is the shape of mistake this project keeps paying
 * for. There is one. */
/** The animation frame a tile shows at tick `t`: the one place the rule lives, so a cache of the tile layer can ask it too. */
export function tileFrame(kind, x, h, t) {
  const a = animOf(kind);
  if (a.frames <= 1) return 0;
  const cycle = a.rate * a.frames;
  const phase = a.phase === 'wave' ? Math.round(x * cycle / (a.span || 8))
    : a.phase === 'scatter' ? h % cycle : 0;
  return Math.floor((t + phase) / a.rate) % a.frames;
}
export function tileHash(ax, ay, x, y, kind) { return mix(ax * 61 + x, ay * 37 + y, kind); }

export function drawOneTile(game, tiles, pal, palKey, x, y, ox = 0, oy = 0, coords) {
  const g = game.g;
  const ax = coords ? coords.x : game.area.x;
  const ay = coords ? coords.y : game.area.y;
  const t = game.frame;
  {
    {
      const kind = tiles[y][x];
      const h = mix(ax * 61 + x, ay * 37 + y, kind);
      let v = VARIED.has(kind) ? h % VARIANTS : 0;
      if (EDGED.has(kind)) {
        // low nibble: which sides border something else, so only the real
        // shoreline gets a rim. high bits: an interior variant, without which
        // every tile of a lake was pixel-identical and read as wallpaper.
        const same = (nx, ny) => (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS)
          || tiles[ny][nx] === kind;
        const mask = (same(x, y - 1) ? 0 : 1) | (same(x + 1, y) ? 0 : 2)
          | (same(x, y + 1) ? 0 : 4) | (same(x - 1, y) ? 0 : 8);
        /* ...AND THE INNER CORNERS, WHICH THE SIDES CANNOT SEE.
         *
         * A four-bit "which sides border something else" mask is the whole of
         * a Wang edge set except for one case, and it is the case that shows:
         * a tile whose four sides all match but whose DIAGONAL does not. That
         * is an inner corner — the place a lake wraps around a headland — and
         * with a side-only mask it gets no treatment at all, so the shoreline
         * runs straight past it and leaves a hard two-pixel notch. It is the
         * commonest defect in a naive tileset and the reason blob sets have
         * 47 tiles rather than 16.
         *
         * A diagonal bit is set only when the diagonal differs AND both of its
         * neighbouring sides match, which is precisely the inner-corner case;
         * where a side already differs, the side's own fraying covers it. */
        /* ...AND WHICH OF THOSE SIDES IS DRY LAND.
         *
         * The mask above says only "a different kind", so a shallow tile
         * cannot tell a bank from open water, and its fraying had to pick one
         * tone for both. It picked `bed2`, a submerged-stone tone -- so the
         * water dissolved into more water and the boundary with the GRASS
         * stayed a ruled line on the tile grid. On a marsh that is a
         * checkerboard: cyan rectangle, green rectangle, cyan rectangle.
         *
         * Four more bits, one per side, saying that side is dry. The water tile
         * then frays into the bank where there is a bank and into its own bed
         * where there is not. */
        const dry = (nx, ny) => {
          if (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS) return false;
          const n = tiles[ny][nx];
          return !(n === T.WATER || n === T.SHALLOW || n === T.SEA);
        };
        const landM = (dry(x, y - 1) ? 1 : 0) | (dry(x + 1, y) ? 2 : 0)
          | (dry(x, y + 1) ? 4 : 0) | (dry(x - 1, y) ? 8 : 0);
        const corner = (dx, dy) => (!same(x + dx, y + dy)
          && same(x + dx, y) && same(x, y + dy)) ? 1 : 0;
        const diag = corner(1, -1) | (corner(1, 1) << 1)
          | (corner(-1, 1) << 2) | (corner(-1, -1) << 3);
        /* ...AND THE CORNERS THAT TOUCH NOTHING BUT EACH OTHER.
         *
         * `corner` above is the INNER corner: a diagonal that differs while
         * both of its sides match, which is where a lake wraps a headland. The
         * case it cannot see is the exact opposite -- a diagonal that MATCHES
         * while both of its sides differ. That is two tiles of the same stuff
         * meeting at a single point, and it is what a stream running slantwise
         * across the grid is made of.
         *
         * Drawn with no help it reads as a dotted line: a row of separate
         * squares, each touching the next at one pixel, which is a picture of
         * beads rather than of water. Reported from play looking at the whole
         * island -- "those diagonal wet tiles don't look good, in general we
         * should never have diagonals like that, there should be thicker
         * diagonals". A four-bit mask of which corners are that case lets the
         * tile fill the corner in and give the diagonal a waist.
         *
         * Bits 12-15. Bits 0-3 are the sides, 4-7 the inner corners, 8-9 the
         * interior variant and 10-11 the sea's shelf. */
        const bridge = (dx, dy) => (same(x + dx, y + dy)
          && !same(x + dx, y) && !same(x, y + dy)) ? 1 : 0;
        const join = bridge(1, -1) | (bridge(1, 1) << 1)
          | (bridge(-1, 1) << 2) | (bridge(-1, -1) << 3);
        // ...and bits 16-19 say which of those sides is dry land, so a water
        // tile frays into the bank where there is one -- see `dry` above
        v = mask | (diag << 4) | ((h % 3) << 8) | (join << 12) | (landM << 16);
        /* THE SEA GETS SHALLOWER AS IT NEARS THE LAND.
         *
         * A coastline drawn as one flat teal against the beach is two pixels
         * of transition on the most important edge in the game. The distance
         * field says how far this water is from the shore, so the tile can be
         * drawn as a shelf: pale and foaming at the edge, darkening outward.
         *
         * It stays `T.SEA` — impassable, opened by no charm — because a
         * WALKABLE shallow along the whole coast is a route round every cliff
         * on the island for whoever holds the tide, which is a gating leak
         * this file has already had once. */
        if (kind === T.SEA && game.world && game.world.coast) {
          const tr2 = game.terrain;
          const d = -game.world.coast[tr2.at(
            Math.max(0, Math.min(tr2.TW - 1, ax * COLS + x)),
            Math.max(0, Math.min(tr2.TH - 1, ay * ROWS + y)))];
          v |= (d <= 1.2 ? 1 : d <= 2.4 ? 2 : d <= 4 ? 3 : 0) << 10;
        }
      }
      // A HEDGE YOU CAN GO UNDER HAS TO LOOK LIKE ONE. Thorn is in neither
      // VARIED nor EDGED, so every thorn tile in a world was byte-identical
      // — and once the generator started growing brambles in the shallows,
      // the one hedge in the room a Silt Lung holder can walk through looked
      // exactly like the twenty that will still stop them. Variant 1 is the
      // hedge with a drop under it. It is a description of the tile, not a
      // caption on it: the player still has to notice.
      /* A STONE KNOWS WHETHER IT IS THE HEAD OR THE FOOT.
       * Variant 0 is the head — there is more stone under it; 1 is the foot.
       * The same trick the shoreline uses, one neighbour instead of four. */
      if (kind === T.STONE) {
        v = (y < ROWS - 1 && tiles[y + 1][x] === T.STONE) ? 0 : 1;
      }
      /* A CLIFF READS THE LAND, NOT THE TILES.
       *
       * Which sides of a cliff tile are higher is a fact about the terrace
       * field, and asking the tiles instead would make it a fact about the
       * screen — the neighbour a tile away is on another screen's array at
       * every boundary, which is how the border wall got written in the first
       * place. The terrain is one grid over the whole world, so this is the
       * same answer from either side of a seam. */
      if (kind === T.CLIFF) {
        const tr = game.terrain;
        if (tr) {
          const wx = ax * COLS + x, wy = ay * ROWS + y;
          const lv = (nx, ny) => tr.terrace[tr.at(
            Math.max(0, Math.min(tr.TW - 1, nx)), Math.max(0, Math.min(tr.TH - 1, ny)))];
          const here = lv(wx, wy);
          v = (lv(wx, wy - 1) > here ? 1 : 0) | (lv(wx + 1, wy) > here ? 2 : 0)
            | (lv(wx, wy + 1) > here ? 4 : 0) | (lv(wx - 1, wy) > here ? 8 : 0);
          // a river that runs off the ground above this face falls down it
          const riv = (nx, ny) => tr.river[tr.at(
            Math.max(0, Math.min(tr.TW - 1, nx)), Math.max(0, Math.min(tr.TH - 1, ny)))];
          if ((v & 1) && riv(wx, wy - 1)) v |= 16;
          /* ...AND WHETHER THE FACE CARRIES ON EITHER SIDE OF THIS TILE.
           *
           * A four-bit "which side is higher" mask says which way a face
           * points and nothing about where it ENDS, so a run of cliff was a
           * row of identical tiles that simply stopped — no return edge, no
           * corner, the wall cut off square in mid-air. The two extra bits are
           * the Wang part: a tile knows whether its neighbours along the run
           * are the same face, and draws a corner where they are not.
           *
           * Read from `terrain.face`, which is the one place that says where
           * the rock is, so the picture cannot disagree with the collision. */
          const isFace = (nx, ny) => tr.face && tr.face[tr.at(
            Math.max(0, Math.min(tr.TW - 1, nx)), Math.max(0, Math.min(tr.TH - 1, ny)))] > 0;
          if (isFace(wx - 1, wy)) v |= 32;
          if (isFace(wx + 1, wy)) v |= 64;
          if (isFace(wx, wy + 1)) v |= 128;   // the face carries on below: a tall one
          /* ...AND WHETHER THE FALL CARRIES ON EITHER SIDE.
           *
           * A cascade down a slanting cliff is a chain of falls that step
           * sideways as they descend, and each tile drew its own ribbon and
           * pool ending square at its own edge -- so a diagonal cascade came
           * out as a line of separate blue postage stamps touching at their
           * corners. Reported from play, looking at the whole island: "those
           * diagonal wet tiles don't look good ... there should be thicker
           * diagonals". Bits 256 and 512 let a fall spill toward the tile the
           * water is actually going to, which gives the chain a waist. */
          const falls = (nx, ny) => isFace(nx, ny) && lv(nx, ny - 1) > lv(nx, ny)
            && riv(nx, ny - 1);
          /* ...INCLUDING DIAGONALLY, WHICH IS THE WHOLE CASE. A cascade down a
           * slanting cliff steps ONE ACROSS AS IT DROPS, so the next fall is
           * rarely due west or east -- it is down-west or down-east, and the
           * first cut of this checked only the two orthogonal neighbours and
           * changed nothing at all. Up-west and up-east are here too, so the
           * tile receiving the water opens the same side the tile above it
           * spills from. */
          const fallsSide = (dx) => falls(wx + dx, wy) || falls(wx + dx, wy + 1)
            || falls(wx + dx, wy - 1);
          if (fallsSide(-1)) v |= 256;
          if (fallsSide(1)) v |= 512;
          /* AND WHETHER THIS ROCK IS A WALL AT ALL.
           *
           * Bit 1024 says the formation this tile belongs to is under four
           * tiles — see the note in `terrain.ts`. Those are drawn as outcrops
           * rather than as walls, because a one-tile wall is a plank lying in
           * the field, and bit 2048 completes the neighbour set the outcrop
           * needs so a two- or three-tile crag joins into one mass instead of
           * reading as separate boulders touching at their corners. */
          if (tr.crag && tr.crag[tr.at(
            Math.max(0, Math.min(tr.TW - 1, wx)), Math.max(0, Math.min(tr.TH - 1, wy)))]) v |= 1024;
          if (isFace(wx, wy - 1)) v |= 2048;
          /* HOW LONG THIS WALL ACTUALLY IS (LS-199). Reported from play with
           * a screenshot and four arrows: "what are these random ledges, they
           * really mess with my sense of depth perception". Every arrow was on
           * a SHORT RUN of wall -- one, two or three tiles of face standing in
           * open dirt with nothing either side. `terrain.ts` already catches
           * small FORMATIONS (bit 1024) and draws them as outcrops, but a big
           * formation can still present a stub of visible face, and a stub of
           * masonry lying in a field is a crate: the lit brink reads as a top
           * face, so it promises a step onto a level that is not there.
           *
           * Bit 4096 says the visible run through this tile is three or fewer,
           * counted along the row here where the neighbours are already being
           * asked about. The foot, the collision and the tiers do not move --
           * only the picture, from masonry to rock. */
          if ((v & 1) && tr.stubAt && tr.stubAt(wx, wy)) v |= 4096;
        } else v = 1;
      }
      // a stair climbs toward the higher ground beside it
      if (kind === T.RAMP) {
        const tr3 = game.terrain;
        if (tr3) {
          const wx = ax * COLS + x, wy = ay * ROWS + y;
          const lv3 = (nx, ny) => tr3.terrace[tr3.at(
            Math.max(0, Math.min(tr3.TW - 1, nx)), Math.max(0, Math.min(tr3.TH - 1, ny)))];
          const here3 = lv3(wx, wy);
          v = (lv3(wx, wy - 1) > here3 ? 1 : 0) | (lv3(wx + 1, wy) > here3 ? 2 : 0)
            | (lv3(wx, wy + 1) > here3 ? 4 : 0) | (lv3(wx - 1, wy) > here3 ? 8 : 0);
        } else v = 1;
      }
      if (kind === T.THORN) {
        v = thornVariant(tiles, x, y);
      }
      const f = tileFrame(kind, x, h, t);
      const wild = game.area;
      const bare = BARE_IN_WILD.has(kind) && wild;   // the field's ground is under it (art.ts, LS-218)
      const flat = wild && (kind === T.WATER || kind === T.SEA) && !(globalThis as any).__noField;   // the sea's shelf is the field's per pixel, not the cell's in three steps
      g.drawImage(bare ? tile(kind, { ...pal, noBase: true }, palKey + '~', v, f) : flat ? tile(kind, { ...pal, noShelf: true }, palKey + '~s', v, f) : tile(kind, pal, palKey, v, f), Math.round(x * TS + ox), Math.round(y * TS + oy));
      /* AND WHAT IS WRITTEN ON IT.
       *
       * The stone is baked with the tile; the MARKS are drawn here because
       * only the game knows which charm this barrier answers to, and that is
       * a per-seed permutation. Two glyphs — one word of deepmark — cut into
       * the shaft. `world.stones` is keyed by screen and tile, so a stone
       * says the same thing every time you come back to it.
       */
      if (kind === T.STONE && game.world && game.world.stones) {
        /* THE WHOLE STONE IS ONE SURFACE, AND THE INSCRIPTION RUNS DOWN IT.
         *
         * This drew one mark per TILE — the head cut the first syllable of
         * the first word and the foot the first syllable of the second — so a
         * face could only ever carry one of {na, te} over one of {su, ke, me,
         * ro}. Six of the corpus's eleven marks, and `ko ra no la si` existed
         * nowhere in the world: they were drawn on the map's key and in the
         * lean-in panel, both of which are UI, so more than half the script
         * was learnable and not encounterable. Measured over 878 stones on 24
         * seeds: 6 of 11 marks reachable, and `na` 49% of every mark cut.
         *
         * `world.stones` is keyed on the FOOT, and the foot is drawn after
         * the head (both callers walk y ascending), so the foot cuts the
         * whole inscription and the head cuts nothing. That is what lets a
         * mark straddle the tile seam, which the middle one has to.
         */
        const isHead = y < ROWS - 1 && tiles[y + 1][x] === T.STONE;
        const charm = isHead ? null
          : game.world.stones.get(ax + ',' + ay + '|' + x + ',' + y);
        const said = charm && bearingWords(game.world, ax, ay, charm);
        if (said) {
          /* HOW MUCH FACE THERE IS, taken from the tile the marks are cut
           * into rather than from the tile grid.
           *
           * `art.ts` paints the pale band at columns 5..11 — SEVEN pixels
           * wide, not sixteen — running the head's rows 4..15 and the foot's
           * rows 0..11. So two marks side by side is not a layout that exists
           * here at any legible size: a 7px figure is 6 columns, and two of
           * them need thirteen. Rendered and looked at: both marks land on the
           * dark edge and the ground, and the face itself comes out blank.
           *
           * Down the shaft there are 24 continuous rows on a two-tile stone
           * and 12 on a one-tile one, which at the size the alphabet needs is
           * three marks and one. Every stone measured is two tiles — soak
           * asserts it — but `world.ts` still places a single tile when the
           * head is blocked, so the short case is answered rather than
           * assumed away, and it is answered by dropping the marks the world
           * says least: a short stone keeps the bearing and loses the noun. */
          const tall = y > 0 && tiles[y - 1][x] === T.STONE;
          const marks = faceMarks(said, tall ? FACE_ROWS_TALL : FACE_ROWS_SHORT);
          /* Cut, not painted: near-black on the pale face, because an incised
           * mark is a shadow. */
          const left = Math.round(x * TS + ox) + FACE_X;
          const top = Math.round(y * TS + oy)
            + (tall ? STONE_FACE.headTop - TS : STONE_FACE.shortTop);
          marks.forEach((m, i) => drawGlyph(g, glyphOf(m), left,
            top + i * (FACE_SIZE + FACE_GAP), FACE_SIZE, '#1a1622'));
        }
      }
    }
  }
}

/** `size` is kept for call-site compatibility: 12+ means the double-size
 *  face. Everything lands on integer pixels. */
export function text(game, str, x, y, col = UI.paperDim, size = 8) {
  return drawText(game.g, str, x, y, col, size >= 12 ? 2 : 1);
}

/** The panel's own short form for a verb too long to be cut inside the plate.
 *  Exported because the check that holds the plate must read THIS table: a
 *  check with its own copy of it passes while the panel says anything at all. */
/* THE PANEL'S SHORT FORMS, and they stop at the panel (the simulation's verbs are what things answer: fists cut no
 * grass because their verb is 'strike'). 'hit' for strike since round 53, and 'stab' for pierce since 2026-09-15 --
 * the second rank of weapons brought FRACTIONAL damage to a pierce, and 'PIERCE 1.2' measures 59 px in a 56 px
 * plate, over the carved border at both ends. 'STAB 1.2' is 47. */
export const PANEL_VERB: Record<string, string> = { strike: 'hit', pierce: 'stab' };
export const panelVerb = (v: string) => PANEL_VERB[v] || v;

export function textWidth(game, str, size = 8) { return glyphWidth(String(str), size >= 12 ? 2 : 1); }

/** Centred horizontally in the 256px screen. */
export function textMid(game, str, y, col, size = 8) {
  // ...AND IT HAS TO FIT. A seed is the one string in the game the PLAYER
  // writes, the field has no length limit, and a 40-character one measures
  // 269px on a 256px screen — `(VW - 269) / 2` is -6, so it starts seven
  // pixels off the left edge and runs seven off the right. Trim to what the
  // screen can hold and say it was trimmed.
  let out = String(str);
  if (game.textWidth(out, size) > VW - 8) {
    while (out.length > 1 && game.textWidth(out + '..', size) > VW - 8) out = out.slice(0, -1);
    out += '..';
  }
  return game.text(out, Math.round((VW - game.textWidth(out, size)) / 2), y, col, size);
}

/* THE STRIP (2026-09-27, LS-475; Vijay: "how would you improve the visuals" -- "do all of this"). The plate was 72 of
 * the 264 rows: a vigor box, a panel of weapon words, two rows of pack, a caption row, a dial and a country tag -- a
 * quarter of the picture, much of it words the no-text rule had already ruled out. It is 24 rows now: the leaves with
 * a chip for each thing that ails you, the hotbar with the picked blade's wear under it, the three beasts and the
 * hour. The second row of the pack is in the bag; the old plate is drawHudTall, kept for anyone bringing a piece back. */
const AIL_CHIP: Record<string, string> = { Soaked: '#5fa8d8', Chilled: '#cfe6f2', Scorched: '#e0703a', Poisoned: '#7cc25a', Bleeding: '#d8453a', Choking: '#9a9aa2', Spored: '#b89ad0' };
export function drawHud(game) {
  const g = game.g, bag = bagOf(game.state);
  g.fillStyle = PLATE_DEEP; g.fillRect(0, 0, VW, HUD_H);
  slab(g, 2, 2, 54, HUD_H - 4, 'bone', PLATE_BODY);
  slab(g, 57, 2, 140, HUD_H - 4, 'bronze', WELL_FLOOR);
  slab(g, 198, 2, 56, HUD_H - 4, 'bone', PLATE_BODY);
  /* the leaves: five, each a fifth of the vigor, red on the frame an affliction takes one */
  const LEAVES = 5, pips = Math.min(LEAVES, Math.max(1, Math.ceil(game.state.maxHp / 2))), per = game.state.maxHp / pips;
  const ail = !!(game.player && game.player.ailFlash && game.player.ailFlash.t > 0);
  for (let i = 0; i < pips; i++) {
    const x = 7 + i * 9, share = Math.max(0, Math.min(1, (game.state.hp - i * per) / per));
    drawLeaf(g, x, 6, 8, PLATE_LIT, EDGE_SHADE); drawLeaf(g, x, 5, 8, PIP_SOCKET, EDGE_SHADE);
    if (share > 0) { g.save(); g.beginPath(); g.rect(x, 5, Math.max(1, Math.round(8 * share)), 8); g.clip(); drawLeaf(g, x, 5, 8, ail ? UI.danger : UI.leaf, ail ? '#7a2f22' : UI.leafDim); g.fillStyle = ail ? '#ffd9c0' : UI.leafLit; g.fillRect(x + 2, 6, 3, 1); g.fillRect(x + 2, 7, 1, 1); g.restore(); }
  }
  /* what ails you: a chip of its colour under the leaves, not a red word at the other end of the bar */
  { const names = game.player ? activeNames(game.player) : []; let x = 7;
    for (const n of names) { const c = AIL_CHIP[n] || UI.danger; g.fillStyle = '#241a24'; g.fillRect(x - 1, 15, 7, 5); g.fillStyle = c; g.fillRect(x, 16, 5, 3); x += 8; if (x > 50) break; } }
  /* the hotbar, and the picked blade's wear as a line under its slot */
  drawHotbar(game, g, HOTBAR_X, HOTBAR_Y, false);
  { const i = bag.selected; const sl = i < HOTBAR ? bag.slots[i] : null; const d = sl && ITEMS[sl.kind];
    if (d && d.dur && sl.dur !== undefined) { const x = HOTBAR_X + i * HOTBAR_PITCH, f = Math.max(0, Math.min(1, sl.dur / d.dur)); g.fillStyle = '#241a24'; g.fillRect(x, HOTBAR_Y + 15, 14, 2); g.fillStyle = f > 0.3 ? UI.gold : UI.danger; g.fillRect(x, HOTBAR_Y + 15, Math.max(1, Math.round(14 * f)), 1); } }
  { const would = game.useWould ? game.useWould() : null;
    if (would && bag.selected < HOTBAR) { const x = HOTBAR_X + bag.selected * HOTBAR_PITCH, y = HOTBAR_Y; const on = Math.sin(game.frame / 7) > 0;
      g.fillStyle = on ? UI.paper : UI.gold; g.fillRect(x - 1, y - 1, 16, 1); g.fillRect(x - 1, y + 15, 16, 1); g.fillRect(x - 1, y - 1, 1, 17); g.fillRect(x + 14, y - 1, 1, 17); } }
  { const ANSWER: Record<string, string> = { sow: 'axe', oldshell: 'spear', bramblemother: 'torch' };
    const open = (game.enemies || []).find((e: any) => e && !e.dead && e.def && e.def.beast && (e.state === 'spent' || e.state === 'wind' || e.reel > 0 || e.stun > 0 || e.flipped > 0 || e.bogged > 0));
    const want = open ? ANSWER[open.type] : null;
    if (want) for (let i = 0; i < HOTBAR; i++) { const sl = bag.slots[i]; if (!sl || sl.kind !== want) continue; const x = HOTBAR_X + i * HOTBAR_PITCH, y = HOTBAR_Y, on = Math.sin(game.frame / 4) > 0;
      g.fillStyle = on ? UI.gold : UI.paper; g.fillRect(x - 1, y - 1, 16, 1); g.fillRect(x - 1, y + 15, 16, 1); g.fillRect(x - 1, y - 1, 1, 17); g.fillRect(x + 14, y - 1, 1, 17); } }
  /* the three beasts, gold when felled */
  beastMarks(game, g, 201, 7);
  /* the hour: a small face that darkens with the sky, the day's hand, and the needle toward the beast last heard */
  { const cx = 246, cy = 12, radius = 7, dark = Math.max(0, Math.min(1, (game.skyNow ? game.skyNow().dark : 0) || 0));
    const circle = (r: number, color: string) => { g.fillStyle = color; for (let y = -r; y <= r; y++) { const half = Math.floor(Math.sqrt(r * r - y * y)); g.fillRect(cx - half, cy + y, half * 2 + 1, 1); } };
    circle(radius, BONE_INK); circle(radius - 1, mixHex(BONE_DIM, '#1c1830', dark)); circle(radius - 2, mixHex(BONE, '#5a5490', dark));
    const angle = (game.skyNow ? game.skyNow().day : 0) * Math.PI * 2; g.fillStyle = BONE_DARK;
    for (let r = 1; r <= 4; r++) g.fillRect(Math.round(cx + Math.sin(angle) * r), Math.round(cy - Math.cos(angle) * r), 1, 1);
    const last = game.needleBeast ? game.needleBeast() : null, lair = last && game.world && game.world.lairs ? game.world.lairs.find((l: any) => l.kind === last.kind) : null;
    if (lair && game.player) { const ddx = lair.px - game.player.cx, ddy = lair.py - game.player.cy, len = Math.hypot(ddx, ddy) || 1; g.fillStyle = UI.gold; g.fillRect(Math.round(cx + ddx / len * 6) - 1, Math.round(cy + ddy / len * 6) - 1, 2, 2); } }
  if (game.fps && (game.debug || game.showFps)) smallText(g, Math.round(game.fps) + ' Q' + (game.quality ?? 3), 200, 17, BONE_LIT, 30);
  drawWorldWords(game, g);   // the toast and the stone's marks (2026-09-28: lost with the strip)
}
/** The old 72-row plate, drawn by nothing since the strip (2026-09-27). */
/** WHAT THE GAME SAYS AND WHAT A STONE SHOWS (moved out of drawHudTall, 2026-09-28): the strip HUD never called that
 *  painter, so since the strip (2026-09-27) no toast and no leaned-in stone was drawn at all. Both HUDs call this. */
export function drawWorldWords(game: any, g: any) {
  const stones = game.world && game.world.stones;
  if (stones) {
    const found = stoneUnderfoot(game);
    const reading = found.reading, at = found.at;
    const py2 = Math.floor((game.player.cy - (game.inkUiOn ? 18 : 0)) / TS);   // the body's own row, in the screen's tiles, for the panel's placement below -- its head's in the ink look, where the painted body stands a tile and a half tall and the panel sat on its hood
    const saidNear = reading && bearingWords(game.world, found.sx ?? game.area.x, found.sy ?? game.area.y, reading);
    if (saidNear) {
      const syls = saidNear.flatMap((w) => syllablesOf(w));
      const size = 18, cw = Math.round(size * 7 / 9), gap = 5;
      const w = syls.length * (cw + gap) - gap;
      /* IN THE WINDOW, NOT IN THE SCREEN'S TILES (2026-09-18). `stoneUnderfoot` answers the stone's tile INSIDE its
       * screen, and the glass shows `world - camera`, which never aligns to a screen: the panel landed up to 57 px
       * across and 52 px down from the stone it belongs to, and on one stone it covered the body reading it. The
       * gloom ten screens up this file already adds the same offset. */
      const camP = game.camera(), offX = -camP.x, offY = -camP.y;   // `at` is a world tile (LS-511)
      let x0 = Math.round(offX + at[0] * TS + TS / 2 - w / 2);
      x0 = Math.max(4, Math.min(VW - w - 4, x0));
      /* CLEAR OF THE STONE *AND* CLEAR OF THE PLAYER.
       *
       * This measured `at[1]` alone — the tile the stone was found on — and
       * put the panel in the 26px directly above it. To trigger the panel by
       * standing above the stone you occupy exactly that band: measured, 14
       * of the player's 16 rows sat under the black plate, leaving a chin and
       * a pair of boots. The one position you have to take to read the
       * inscription was the one position the inscription covered.
       *
       * So clear the topmost of the three things that matter — the stone's
       * head, the tile the stone was found on, and the player — and the panel
       * is out of the way whichever side you walk up from.
       */
      let top = Math.min(at[1], py2);
      while (stoneAtWorld(game, at[0], top - 1)) top--;
      const above = offY + top * TS - size - 12;
      const y0 = HUD_H + (above > 4 ? above : offY + Math.max(at[1], py2) * TS + TS + 6);
      /* WHAT THE WORDS NAME AND WHICH WAY (2026-09-29, the playthrough: a first stone showed four glyphs and nothing a new
         player could read, and the dial's red mark it set was a dot on a clock). The ink panel is handed the beast the
         stone names and the true bearing from where you stand, and draws its likeness before the words and an arrow
         after them: the word is learned by standing beside its picture, the way the map key teaches it. */
      let beast: string | null = null, ang: number | null = null;
      { const lairs = game.world.lairs || []; const lair = lairs.find((l: any) => WORDS[l.kind] && saidNear[0] === WORDS[l.kind]);
        if (lair) { beast = lair.kind; if (saidNear.length > 1) { const wx = game.player.cx, wy = game.player.cy; ang = Math.atan2((lair.y + 0.5) * PH - wy, (lair.x + 0.5) * PW - wx); } } }
      if (game.inkUiOn) { (game.inkPlay ||= {}).stone = { x0, y0, w, size, cw, gap, glyphs: syls.map((syl) => glyphOf(syl)), beast, ang }; }   // the ink look letters it at the glass's own size (inkui.drawPlayInk)
      else {
      g.fillStyle = 'rgba(14,12,18,0.85)';
      g.fillRect(x0 - 5, y0 - 4, w + 10, size + 8);
      g.fillStyle = UI.stone;
      g.fillRect(x0 - 5, y0 - 4, w + 10, 1);
      g.fillRect(x0 - 5, y0 + size + 3, w + 10, 1);
      syls.forEach((syl, i2) => {
        drawGlyph(g, glyphOf(syl), x0 + i2 * (cw + gap), y0, size, UI.paper);
      }); }
    }
  }
  if (game.message && !game.paused && !game.bagOpen && !game.bandShown) {   // not over the map or the bag, whose titles sit where it sits; nor over the picture when a phone's band carries it
    /* THE TOAST IS WRAPPED, NOT CLIPPED (the bug bash, 2026-09-06). The band was sized to the text and centred on the
     * picture, with nothing bounding either -- so `the stones remember a name you have not heard` measured 264 pixels
     * in a 256-pixel screen, started at x -11 and ran off BOTH edges: the reader lost the first letter and the last
     * word. A toast is prose the game speaks, so it wraps onto a second line rather than losing its end to an
     * ellipsis; past two lines the tail is trimmed, because the band cannot eat the picture. */
    const MAXW = VW - 24;
    if (game.inkUiOn) { (game.inkPlay ||= {}).toast = { text: game.message.text }; return; }   // the ink look sets it in type on a paper tag (inkui.drawPlayInk)
    const lines = wrapLines(game, game.message.text, MAXW);
    const w = Math.min(VW - 4, Math.max(...lines.map((ln) => game.textWidth(ln, 8))) + 14);
    const x = Math.max(2, Math.round(VW / 2 - w / 2));
    const h = 16 + (lines.length - 1) * 11;
    g.fillStyle = 'rgba(14,12,18,0.9)';
    g.fillRect(x, HUD_H + 5, w, h);
    g.fillStyle = UI.violet;
    g.fillRect(x, HUD_H + 5, w, 1);
    g.fillRect(x, HUD_H + 4 + h, w, 1);
    lines.forEach((ln, i) => game.text(ln, Math.round(x + w / 2 - game.textWidth(ln, 8) / 2), HUD_H + 9 + i * 11, UI.paperDim, 8));
  }
}

export function drawHudTall(game) {
  const g = game.g, bag = bagOf(game.state);
  g.fillStyle = PLATE_DEEP; g.fillRect(0, 0, VW, HUD_H);
  slab(g, 2, 2, 56, 27, 'bone', PLATE_BODY);
  slab(g, 2, 29, 56, 41, 'bone');
  slab(g, 58, 2, 144, 68, 'bronze', PLATE_BODY);
  slab(g, 202, 2, 52, 68, 'bone', PLATE_BODY);

  // Recessed weave, behind the inventory and its projecting nameplate.
  g.fillStyle = PLATE_KNOT;
  for (let y = 7; y < 60; y += 7) for (let x = 65; x < 196; x += 9) {
    g.fillRect(x, y, 3, 1); g.fillRect(x + 3, y + 1, 1, 2);
    g.fillRect(x + 1, y + 3, 3, 1);
  }
  /* EVERYTHING IS LOWERCASE (2026-09-10, Vijay: "everything should be all
   * lowercase"). The bar shouted -- VIGOR, SPEAR PIERCE 1, 110 OF 110 BLOWS,
   * HEATH -- while every other surface in the game, the title and the toasts
   * and the manual, spoke in lower case. It was ten `toUpperCase()` calls and
   * four shouted literals, and the golden frames were re-blessed for it. */
  // ...or the name of the piece that just kept something off it, in gold (LS-298)
  const saved = game.gearSave && game.gearSave.until > game.runFrames ? game.gearSave.kind : null;
  centered(g, saved || 'vigor', 7, 8, 46, saved ? UI.gold : BONE);
  /* FIVE LEAVES, ALWAYS (2026-09-18). A pip was two vigor and the row was pinned to 45 px, so every Great Beast felled
   * -- which is two more vigor -- put another pip in the same space: at twelve the row was leaf, bar, leaf, bar; at
   * sixteen there was no leaf left, only rectangles two rows higher up, four or five pixels wide; at the cap they were
   * two pixels. The bar that says what you are stopped being a picture of anything halfway through the run it
   * measures. Five leaves is what the well holds at the leaf's own size, so five is what it draws, and a leaf carries
   * a fifth of the vigor -- two at the start, which is exactly the opening picture, and more as the beasts fall. */
  const LEAVES = 5;
  const pips = Math.min(LEAVES, Math.max(1, Math.ceil(game.state.maxHp / 2)));
  const per = game.state.maxHp / pips;   // what one leaf carries
  /* THE LEAVES GO RED ON THE FRAME AN AFFLICTION TAKES ONE (2026-09-10). The
   * vigor bar is at one end of the HUD and the word for what is eating it at
   * the other, six seconds apart in time; lighting both at the same instant
   * in the same colour is the whole explanation. */
  const ail = !!(game.player && game.player.ailFlash && game.player.ailFlash.t > 0);
  for (let i = 0; i < pips; i++) {
    const x = 8 + i * 9;
    const share = Math.max(0, Math.min(1, (game.state.hp - i * per) / per));
    drawLeaf(g, x, 19, 8, PLATE_LIT, EDGE_SHADE);
    drawLeaf(g, x, 18, 8, PIP_SOCKET, EDGE_SHADE);
    if (share > 0) {
      g.save(); g.beginPath(); g.rect(x, 18, Math.max(1, Math.round(8 * share)), 8); g.clip();
      drawLeaf(g, x, 18, 8, ail ? UI.danger : UI.leaf, ail ? '#7a2f22' : UI.leafDim);
      g.fillStyle = ail ? '#ffd9c0' : UI.leafLit; g.fillRect(x + 2, 19, 3, 1); g.fillRect(x + 2, 20, 1, 1); g.restore();
    }
  }

  const weapon = wieldedWeapon(game.state);
  const weaponName = weapon.kind === 'fist' ? 'hands' : weapon.name;
  const forge = game.forgePreview?.();
  const goalShown = drawForgeGoal(game, forge);
  if (!goalShown) centered(g, weaponName, 7, 35, 46, BONE_INK);
  /* THE PLATE IS 56 PIXELS WIDE AND 'STRIKE 0.5' IS 59 (2026-09-07). Bare
   * hands are the only weapon whose stat line does not fit: the verb is the
   * longest in the table and the damage is the only fractional one, so the
   * line is drawn from x0.5 to x59.5 across a plate that runs x2 to x58 --
   * over the carved border at both ends, which is what every other readout in
   * this HUD is built to sit inside.
   *
   * The verb itself is NOT renamed. `strike` is load-bearing in the
   * simulation: it is the verb nothing answers, which is why fists cut no
   * grass, pass no guard, and crush and burn nothing. This is the panel's own
   * short form and goes no further than the panel. */
  /* THE NUMBERS WAIT FOR THE FIRST BLOW (LS-397, 2026-09-12; the deeper pass: "the HUD opens in numbers"). Cut and reach
   * mean nothing until something has been hit; until then the panel names the thing in hand and no more. */
  const numbersOn = (game.state.blows || 0) > 0 || (game.state.beastsSlain || []).length > 0;
  if (numbersOn && !goalShown) centered(g, panelVerb(weapon.weapon.verb) + ' ' + weapon.weapon.damage, 7, 46, 46, BONE_INK);
  if (numbersOn && !goalShown) centered(g, 'reach ' + weapon.weapon.reach, 7, 56, 46, BONE_INK);

  slab(g, 59, 17, 142, 44, 'bronze', WELL_FLOOR);
  drawHotbar(game, g, HOTBAR_X, HOTBAR_Y, false);
  for (let i = 0; i < HOTBAR; i++) {
    drawSlot(game, g, HOTBAR_X + i * HOTBAR_PITCH, 42, bag.slots[HOTBAR + i], false);
    drawPickupCue(game, g, HOTBAR_X + i * HOTBAR_PITCH, 42, bag.slots[HOTBAR + i]);
  }
  /* THE PICKED SLOT BREATHES WHEN THE KEY WOULD USE IT (LS-397): eat when hurt or ailing, wear, whet, mend, glim's buys. */
  { const would = game.useWould ? game.useWould() : null;
    if (would) { const i = bag.selected; const x = HOTBAR_X + (i % HOTBAR) * HOTBAR_PITCH, y = i < HOTBAR ? HOTBAR_Y : 42; const on = Math.sin(game.frame / 7) > 0;
      g.fillStyle = on ? UI.paper : UI.gold; g.fillRect(x - 1, y - 1, 16, 1); g.fillRect(x - 1, y + 15, 16, 1); g.fillRect(x - 1, y - 1, 1, 17); g.fillRect(x + 14, y - 1, 1, 17); } }
  /* THE ANSWER GLOWS IN THE HAND (LS-397, 2026-09-12): while a Great Beast on this screen is open -- spent, winding,
   * reeling, stunned or flipped -- the slot holding its answer wears a pulsing gold frame. The fight's whole rule,
   * shown at the moment it applies, without a word. */
  { const ANSWER: Record<string, string> = { sow: 'axe', oldshell: 'spear', bramblemother: 'torch' };
    const open = (game.enemies || []).find((e: any) => e && !e.dead && e.def && e.def.beast && (e.state === 'spent' || e.state === 'wind' || e.reel > 0 || e.stun > 0 || e.flipped > 0));
    const want = open ? ANSWER[open.type] : null;
    if (want) for (let i = 0; i < HOTBAR * 2; i++) {
      const sl = bag.slots[i]; if (!sl || sl.kind !== want) continue;
      const x = HOTBAR_X + (i % HOTBAR) * HOTBAR_PITCH, y = i < HOTBAR ? HOTBAR_Y : 42;
      const on = Math.sin(game.frame / 4) > 0;
      g.fillStyle = on ? UI.gold : UI.paper;
      g.fillRect(x - 1, y - 1, 16, 1); g.fillRect(x - 1, y + 15, 16, 1); g.fillRect(x - 1, y - 1, 1, 17); g.fillRect(x + 14, y - 1, 1, 17);
    } }
  /* THE PLATE SAYS WHAT THE ONE BUTTON WILL DO (LS-374, 2026-09-10), or names
   * the strip when the answer is "swing what you are holding" -- the weapon
   * panel on the left already says which weapon and what it does. It used to
   * show the TOOL's verb whatever was picked, so it read `pod` with a sickle
   * in hand and a sap in the slot beside it. */
  const owned = game.ownedTools(), now = game.equipped();
  const use = game.pressIsUse(), target = use ? game.actTarget() : null;
  const action = use ? (target?.kind === 'forge' ? (target.action === 'take' ? 'take' : 'lay') : actionLabel(now, game.state, game.toolContext()).text) : 'pack';   // the phone's own button already read 'take' here (2026-09-20): the desk plate said 'lay' while the key took the pile
  slab(g, 96, 0, 69, 17, 'bone');
  centered(g, fitCaption(game, action, 57), 101, 5, 59, game.canUseTool() ? BONE_INK : BONE_INK_DIM);
  if (now === 'ember' && game.state.emberCool > 0) {
    g.fillStyle = GOLD_DEEP; g.fillRect(102, 13, Math.round(57 * game.state.emberCool / EMBER_COOL), 1);
  }

  const cx = 228, cy = 24, radius = 19;
  const circle = (r: number, color: string, oy = 0) => {
    g.fillStyle = color;
    for (let y = -r; y <= r; y++) {
      const half = Math.floor(Math.sqrt(r * r - y * y));
      g.fillRect(cx - half, cy + y + oy, half * 2 + 1, 1);
    }
  };
  /* THE DIAL KEEPS THE WORLD'S HOUR (the art lens, round 51). It was the biggest, brightest object in the game --
   * 39 px at a mean luma of 200 -- and the only one carrying no information: it measured the same at noon and in
   * full night. The face darkens with the sky now, so the brightest thing in the frame is not a lamp that ignores
   * the hour it is showing.
   *
   * IT DARKENS BY `sky.dark`, NOT BY `game.night`, and the difference is the whole fix. `game.night` is a WORLD MOD
   * -- "this is a night world" -- and `skyNow()` reads `skyAt(this.night ? NIGHT_DAY : clk.day)`, so a normal world
   * at midnight has `night` FALSE and an hour that is pitch black. The first cut used the flag and was caught by
   * looking at a screenshot: the world was plainly dark, the clock read 16:49, and the face was still bone. `dark`
   * is the sky's own 0-to-1, so the dial dims WITH the hour instead of snapping at a threshold that never came. */
  const dark = Math.max(0, Math.min(1, (game.skyNow ? game.skyNow().dark : 0) || 0));
  circle(radius, BONE_INK, 1); circle(radius - 1, BONE_DARK);
  circle(radius - 2, mixHex(BONE_DIM, '#1c1830', dark)); circle(radius - 3, mixHex(BONE_LIT, '#3a3462', dark));
  circle(radius - 4, mixHex(BONE, '#5a5490', dark)); circle(radius - 5, mixHex('#dac9a6', '#3a3462', dark));
  for (let i = 0; i < 12; i++) {
    const angle = i * Math.PI / 6;
    g.fillStyle = BONE_DARK;
    g.fillRect(Math.round(cx + Math.sin(angle) * 13), Math.round(cy - Math.cos(angle) * 13), i % 3 === 0 ? 2 : 1, i % 3 === 0 ? 2 : 1);
  }
  // Cross-fading ink against a fading face erased the time at dawn/dusk.
  // Switch at this palette's contrast crossover; the face still fades smoothly.
  const time = clock(game.runFrames || 0);
  const ink = dark < 0.58 ? '#000000' : '#ffffff';
  if (game.textWidth(time, 8) <= 29) centered(g, time, cx - 15, cy - 3, 31, ink);
  else smallText(g, time, cx - Math.floor((time.length * 4 - 1) / 2), cy - 2, ink, 31);
  /* ...AND ITS HAND TURNS ONCE A DAY, NOT ONCE A MINUTE (the art lens, round 51). This read
   * `runFrames / 3600`, and 3600 frames is a game-MINUTE with twenty-four to a day -- so the hand went round
   * TWENTY-FOUR TIMES a day, agreeing with neither the time printed inside it nor the sky outside the window. A
   * dial that spins is furniture; a dial that keeps the hour is an instrument. `skyNow().day` is the fraction of
   * the day the whole world already runs on: the sky, the temperature, the tide and the beasts' roaming. */
  const angle = (game.skyNow ? game.skyNow().day : 0) * Math.PI * 2;
  g.fillStyle = BONE_LIT; g.fillRect(Math.round(cx + Math.sin(angle) * 17), Math.round(cy - Math.cos(angle) * 17), 2, 2);
  /* THE NEEDLE (2026-09-12; Vijay: "the game is quite confusing to play right now"): the beast heard last at a stone, its
   * lair's way from where the body stands, as a gold needle on the dial; gone when that beast falls. Earned by reading. */
  { const last = game.needleBeast ? game.needleBeast() : null;   // the beast heard last, or with the crow charm the nearest that lives
    const lair = last && game.world && game.world.lairs ? game.world.lairs.find((l: any) => l.kind === last.kind) : null;
    if (lair && game.player) {
      const ddx = lair.px - game.player.cx, ddy = lair.py - game.player.cy, len = Math.hypot(ddx, ddy) || 1;
      g.fillStyle = UI.gold;
      for (let r = 3; r <= 11; r++) g.fillRect(Math.round(cx + ddx / len * r), Math.round(cy + ddy / len * r), 1, 1);
      g.fillRect(Math.round(cx + ddx / len * 12) - 1, Math.round(cy + ddy / len * 12) - 1, 3, 3);
    } }

  beastMarks(game, g, 207, 46);
  slab(g, 202, 58, 52, 11, 'bone');   // 58, not 60 (round 52's art lens): at 60 the tag's bottom rows -- the descenders of bloom and dust -- ran under the plate's edge
  /* THE COUNTRY'S NAME IS CENTRED IN ITS OWN TAG, and budgeted to the tag rather than to a number: `WILDS/BLOOM` is
   * eleven letters at four pixels each, which ran to x 250 from a fixed start of 207 and collided with the frame's
   * right-hand moulding. Centring it in the slab and giving it the slab's own interior keeps the longest country
   * name clear of the carving at both ends. */
  /* THE TAG NAMES THE COUNTRY, and only the country. `WILDS/BLOOM` is eleven letters at four pixels each -- forty
   * three -- and the tag's clear interior between the carving at either end is about forty, so the longest country
   * ran its W and its M under the mouldings. Every screen is the wilds; what the tag is for is which country you
   * are standing in. It is centred in the tag so a short name sits where a long one does. */
  /* AND THE COUNTRY IS READ IN THE FACE THAT HAS A LOWER CASE (2026-09-10).
   * The 3x5 face is small caps by necessity -- fifteen pixels cannot hold an
   * 'a' with a bowl -- so `heath` came out HEATH beside a HUD that says
   * `vigor` and `pack` in real lower case, and it read as the one thing on the
   * glass that shouts. Every country is five letters or fewer, which is 29
   * pixels in the 5x8 face against a tag interior of 46, so the word the
   * player actually reads gets the real letterforms and the tiny face keeps
   * the places where the room genuinely is not there. */
  const country = String(game.palKey);
  { const CX = 204, CW = 46, w = country.length * GLYPH_ADVANCE - 1;
    drawText(g, country, CX + Math.floor((CW - w) / 2), 60, BONE_INK, 1); }
  g.fillStyle = PLATE_BODY; g.fillRect(63, 61, 135, 9);
  statusRow(game, forge);
  const fault = game.faultLine();
  if (fault) {
    g.fillStyle = PLATE_DEEP; g.fillRect(62, 62, 137, 8);
    smallText(g, fault[0], 65, 64, fault[1], 131);
  }
  /* ...OR WHENEVER THE ADDRESS ASKS FOR IT (2026-09-14, `?fps=1` -> game.showFps). The 3D view is the only view now,
   * and the one number that cannot be measured from a desk is what a particular phone draws. The menu's `frame:`
   * button reports it on a tap, but a tap is not always available -- an iOS simulator can be sent a URL and
   * photographed and nothing more -- so the address can turn the corner readout on and the number is in the picture. */
  if (game.fps && (game.debug || game.showFps)) {   // debug only otherwise (round 52's art lens): '60 Q1' on the pack plate read as a leak
    /* SHORT ENOUGH TO SAY THE THING IT EXISTS TO SAY (2026-09-06). This read `41FPS Q2` into a budget of 30 pixels,
     * which is seven characters in the three-by-five face -- so `smallText` cut it and drew `41FPS..`, and the
     * QUALITY STEP, the one fact a readout for a phone in trouble is for, was the half that got thrown away. The
     * budget is right: the pack's title plate starts at x 96 and the readout starts at 65. So the words go instead
     * of the number. `41 Q2` is five characters and both facts survive. */
    smallText(g, game.fps + ' Q' + (game.quality ?? 3), 65, 9, BONE, 30);
  }
  /* STAND AT A STONE AND YOU CAN READ IT.
   *
   * The marks cut into a standing stone are seven by nine pixels on a
   * sixteen-pixel tile. That is the right size for the WORLD — it says
   * "somebody wrote here" from across a clearing — and too small to learn an
   * alphabet from. Reported from play twice as unclear, and both times the
   * answer I reached for was more contrast or a bigger stone, which treats a
   * reading problem as a drawing problem.
   *
   * A player standing against the stone has chosen to read it, so give them
   * the marks at a size that can be read. Over the stone, not in the HUD: the
   * first version put the panel in the HUD band and it covered the glim and
   * pod counters, and the marks belong to the object anyway — leaning in to
   * look at a carving does not happen in a status bar.
   *
   * Not a caption: no word of any language appears and nothing is explained.
   *
   * It is NOT "the same two marks, closer", which is what this comment used
   * to say and what I believed when I wrote it. The stone's face draws the
   * FIRST SYLLABLE of each word (one 9px mark per tile, which is all a
   * 16-wide face holds); the panel draws every syllable of every word. A
   * lore reviewer rendered both from one seed: the face reads `na su`, the
   * panel reads `na ko su ra`. Leaning in shows you MORE, and two of the four
   * marks it shows are taught nowhere — that is a real gap in the corpus, not
   * a redrawing, and it should not hide behind a comment saying they match.
   */
  drawWorldWords(game, g);
}

const MAP_GROUND = '#322e3a';   // the slate the chart's plates are laid on

/** WHERE THE LAND LIES, FROM A HULL (LS-461, 2026-09-26). At sea the glass is water in every direction: the newcomer probe
 *  paddled past the sow's island without landing and drifted home, with no stone read (no needle) and a foul wind (no
 *  gold ribbons). While aboard, each of the three nearest islands not already on the glass gets a small land-coloured
 *  marker at the edge of the view, on the line from the middle of the view to that island's nearest ground, pulsing
 *  slowly. Nearer islands are drawn larger. Pure geometry of the world's island grid; nothing is read from a plan. */
export function landBearings(game): { x: number; y: number; d: number; id: number }[] {
  if (!game.aboard || !game.world || !game.world.island || (globalThis as any).__noLandBearings) return [];
  const W = game.world.biome[0].length, H = game.world.biome.length, PWp = 16 * 16, PHp = 12 * 16;
  const cam = game.camera(); const cx = cam.x + PWp / 2, cy = cam.y + PHp / 2;
  const best = new Map<number, { px: number; py: number; d: number }>();
  /* the nearest place a hull can put you ashore on each island (game.landings); an island with none keeps its nearest ground */
  const land = game.landings ? game.landings() : new Map();
  for (const [id, list] of land) for (const [wx, wy] of list) {
    const px = wx * 16 + 8, py = wy * 16 + 8, d = Math.hypot(px - cx, py - cy);
    const was = best.get(id); if (!was || d < was.d) best.set(id, { px, py, d });
  }
  for (let sy = 0; sy < H; sy++) for (let sx = 0; sx < W; sx++) {
    const id = game.world.island[sy * W + sx]; if (id < 0 || land.has(id)) continue;
    const x0 = sx * PWp, y0 = sy * PHp;
    const px = Math.max(x0, Math.min(x0 + PWp, cx)), py = Math.max(y0, Math.min(y0 + PHp, cy));
    const d = Math.hypot(px - cx, py - cy);
    const was = best.get(id); if (!was || d < was.d) best.set(id, { px, py, d });
  }
  const out: { x: number; y: number; d: number; id: number }[] = [];
  const hx = PWp / 2 - 7, hy = PHp / 2 - 7;
  for (const [id, b] of [...best].sort((a, c) => a[1].d - c[1].d).slice(0, 3)) {
    const dx = b.px - cx, dy = b.py - cy;
    if (Math.abs(dx) <= PWp / 2 - 6 && Math.abs(dy) <= PHp / 2 - 6) { out.push({ x: PWp / 2 + dx, y: HUD_H + PHp / 2 + dy, d: b.d, id, here: true } as any); continue; }   // the landing is on the glass: marked where it lies
    const k = Math.min(hx / Math.max(1e-6, Math.abs(dx)), hy / Math.max(1e-6, Math.abs(dy)));
    out.push({ x: PWp / 2 + dx * k, y: HUD_H + PHp / 2 + dy * k, d: b.d, id });
  }
  return out;
}
/** A beast's answer on the island the body stands on, out of view: a gold marker at the edge on the line to it (LS-464). */
export function drawAnswerBearings(game) {
  if (game.aboard || (globalThis as any).__noAnswerBeacons) return;
  const g = game.g, cam = game.camera(), PWp = 256, PHp = 192, cx = cam.x + PWp / 2, cy = cam.y + PHp / 2;
  const pulse = 0.7 + 0.3 * Math.sin(game.frame / 16);
  for (const b of game.answerBeacons ? game.answerBeacons() : []) {
    const dx = b.wx - cx, dy = b.wy - cy;
    if (Math.abs(dx) <= PWp / 2 - 4 && Math.abs(dy) <= PHp / 2 - 4) continue;   // on the glass: the glow says it
    const k = Math.min((PWp / 2 - 7) / Math.max(1e-6, Math.abs(dx)), (PHp / 2 - 7) / Math.max(1e-6, Math.abs(dy)));
    const x = Math.round(PWp / 2 + dx * k), y = Math.round(HUD_H + PHp / 2 + dy * k);
    if (game.inkUiOn) { ((game.inkPlay ||= {}).marks ||= []).push({ x, y, kind: 'answer', a: pulse }); continue; }   // the ink look draws it (inkui.drawPlayInk)
    g.globalAlpha = pulse;
    g.fillStyle = '#241a24'; g.fillRect(x - 1, y - 5, 3, 11); g.fillRect(x - 5, y - 1, 11, 3);
    g.fillStyle = '#f2c14e'; g.fillRect(x, y - 4, 1, 9); g.fillRect(x - 4, y, 9, 1); g.fillRect(x - 1, y - 1, 3, 3);   // a gold star: a thing to find, not land
    g.globalAlpha = 1;
  }
}
/** The beast your answer is for: a red star at the edge in its direction (game.quarryBeacons). */
export function drawQuarryBearings(game) {
  if (game.aboard || !game.quarryBeacons) return;
  const g = game.g, cam = game.camera(), PWp = 256, PHp = 192, cx = cam.x + PWp / 2, cy = cam.y + PHp / 2;
  const pulse = 0.7 + 0.3 * Math.sin(game.frame / 12);
  for (const b of game.quarryBeacons()) {
    const dx = b.wx - cx, dy = b.wy - cy;
    if (Math.abs(dx) <= PWp / 2 - 4 && Math.abs(dy) <= PHp / 2 - 4) continue;   // on the glass: the beast says it
    const k = Math.min((PWp / 2 - 7) / Math.max(1e-6, Math.abs(dx)), (PHp / 2 - 7) / Math.max(1e-6, Math.abs(dy)));
    const x = Math.round(PWp / 2 + dx * k), y = Math.round(HUD_H + PHp / 2 + dy * k);
    if (game.inkUiOn) { ((game.inkPlay ||= {}).marks ||= []).push({ x, y, kind: 'quarry', a: pulse }); continue; }
    g.globalAlpha = pulse;
    g.fillStyle = '#241a24'; g.fillRect(x - 1, y - 5, 3, 11); g.fillRect(x - 5, y - 1, 11, 3);
    g.fillStyle = '#e0503a'; g.fillRect(x, y - 4, 1, 9); g.fillRect(x - 4, y, 9, 1); g.fillRect(x - 1, y - 1, 3, 3);   // red: the quarry, not a thing to pick up
    g.globalAlpha = 1;
  }
}
export function drawLandBearings(game) {
  const g = game.g, marks = landBearings(game); if (!marks.length) return;
  const pulse = 0.65 + 0.35 * Math.sin(game.frame / 18);
  for (const m of marks) {
    const r = (m as any).here ? 3 : m.d < 400 ? 4 : m.d < 900 ? 3 : 2;
    if (game.inkUiOn) { ((game.inkPlay ||= {}).marks ||= []).push({ x: m.x, y: m.y, kind: 'land', r, a: pulse }); continue; }
    g.globalAlpha = pulse;
    g.fillStyle = '#241a24'; g.fillRect(Math.round(m.x) - r - 1, Math.round(m.y) - r - 1, r * 2 + 2, r * 2 + 2);
    g.fillStyle = '#e8cf92'; g.fillRect(Math.round(m.x) - r, Math.round(m.y) - r, r * 2, r * 2);
    g.fillStyle = '#7fa35c'; g.fillRect(Math.round(m.x) - r + 1, Math.round(m.y) - r, r * 2 - 2, Math.max(1, r - 1));   // a green crown on the sand: land, not a buoy
  }
  g.globalAlpha = 1;
}

export function drawMap(game) {
  const g = game.g;
  /* BONE PLATES ON STONE. The chart's ground was #12141a, which is black in every light, so whatever was not a plate
   * -- the margins, the gutter between the two plates, the whole footer band -- fell out of the picture. A slate
   * ground is the same move the bar makes: the material under the thing you carry. It is also what turns the mobile
   * lens's number around, and honestly rather than by brightening the wells. */
  g.fillStyle = MAP_GROUND;
  g.fillRect(0, HUD_H, VW, PH);
  g.fillStyle = MATERIAL.stone[0];
  g.fillRect(0, HUD_H, VW, 1); g.fillRect(0, HUD_H + PH - 1, VW, 1);
  /* THE CHART IS A THING YOU CARRY, in the same carved bone the bar and the pack are cut from (2026-09-05). It was a
   * flat black screen with a wireframe on it: the mobile lens measured 1.10:1 between its two commonest colours in
   * the state every run starts in, and 99% of its body under luminance 38 -- the first map a player opens, outdoors,
   * was one dark rectangle with a gold dot. A plate under the grid gives every unwalked cell something to be cut
   * into, and the gloss column gets a plate of its own. */
  const { mx, my, cw, ch, footRows } = mapGeom(game);
  const MAP_WALL = MATERIAL.bone[1], MAP_LIP = MATERIAL.bone[2], MAP_FLOOR = '#191721', MAP_RUMOUR = MATERIAL.bone[2];
  slab(g, mx - 8, HUD_H + 2, WORLD_W * cw + 12, 20, 'bone');
  smallText(g, 'the wilds', mx - 2, HUD_H + 10, BONE_INK, 60);
  slab(g, mx - 8, my - 6, WORLD_W * cw + 12, WORLD_H * ch + 12, 'bone', '#20202a');
  /* THE PLATE AND THE WORDS ON IT ARE ONE GEOMETRY (2026-09-17). The plate is measured from the chart and the
   * glosses were drawn at a hard 196, which was right when a world was ten screens of sixteen pixels. At sixteen by
   * thirteen the cell is twelve pixels and the plate begins at 212, so fourteen of the column's thirty-three inked
   * columns landed on the chart itself and on the title strip. */
  const glossX = mx + WORLD_W * cw + 10;
  { /* the gloss column runs to the chart's own bottom edge: at 92 tall it stopped half way down and left the whole
     * lower right of the screen black, which is the same complaint the lens made about the chart itself */
    slab(g, glossX - 4, HUD_H + 2, VW - glossX + 2, WORLD_H * ch + 30, 'bone', '#20202a'); }   // to the picture's own edge: a word of nine-pixel marks is 34 wide and the plate was 38, so the last mark of the longest word stood a pixel outside it
  for (let y = 0; y < WORLD_H; y++) for (let x = 0; x < WORLD_W; x++) {
    const seen = game.visited.has(x + ',' + y);
    // a screen a stone has named shows as a dim mark, so the map can point
    // you somewhere instead of only recording history
    const charted = game.charted.has(x + ',' + y);
    const rumoured = !seen && charted;
    // A visited screen shows its own country. Sixty-four identical grey
    // squares recorded that you had been somewhere and nothing about where —
    // there was no way to navigate from memory and no reason to go back, and
    // a winning run never enters two thirds of the map.
    const pal = BIOMES[game.world.biome[y][x]] || BIOMES.heath;
    /* THE CHART HAS WATER (2026-09-18). A cell was painted its country's GROUND colour whether or not the screen has
     * any ground: the world is an archipelago of four to six islands in open sea, and 128 of 208 cells on a measured
     * seed are sea -- so the map of a voyage was a green field with green squares on it, and the one thing a sailor
     * needs from a chart, which is where the water is, was the one thing it would not say. A screen off every island
     * is painted in the country's own deep and mid tones, the same two the sea is drawn with on the glass. */
    const wet = !!(game.world.island && game.world.island[y * WORLD_W + x] < 0);
    g.fillStyle = !seen ? (rumoured ? MAP_RUMOUR : MAP_WALL) : (wet ? pal.mid : pal.shade);
    g.fillRect(mx + x * cw, my + y * ch, cw - 2, ch - 2);
    if (!seen && game.world.island) {
      // Geography supports route choice; visiting and charting still reveal content.
      const px = mx + x * cw, py = my + y * ch;
      g.fillStyle = wet ? '#243f46' : '#80906a';
      g.fillRect(px, py, cw, ch);
      if (!wet) {
        const seaAt = (a: number, b: number) => a < 0 || b < 0 || a >= WORLD_W || b >= WORLD_H
          || game.world.island[b * WORLD_W + a] < 0;
        g.fillStyle = '#8d9b73';
        if (seaAt(x, y - 1)) g.fillRect(px, py, cw, 1);
        if (seaAt(x - 1, y)) g.fillRect(px, py, 1, ch);
        g.fillStyle = '#3c5049';
        if (seaAt(x, y + 1)) g.fillRect(px, py + ch - 1, cw, 1);
        if (seaAt(x + 1, y)) g.fillRect(px + cw - 1, py, 1, ch);
      }
    } else if (!seen) {
      /* AN UNWALKED SCREEN IS A WELL CUT IN THE PLATE, not a wire square on black. Three tones make it read as
       * carved: the near wall in bone shade, the floor below the plate, and the far wall catching the light on the
       * bottom row -- the same order slab() cuts its own insets in. */
      g.fillStyle = MAP_FLOOR;
      g.fillRect(mx + x * cw + 1, my + y * ch + 1, cw - 4, ch - 4);
      g.fillStyle = MAP_LIP;
      g.fillRect(mx + x * cw + 1, my + y * ch + ch - 4, cw - 4, 1);
    }
    if (seen) {
      // the WHOLE cell in its ground colour with a one-pixel shade border (LS-234): a three-pixel scrap on a shade field was sixty-four near-identical squares
      g.fillStyle = wet ? pal.deep : pal.ground;
      g.fillRect(mx + x * cw + 1, my + y * ch + 1, cw - 4, ch - 4);
    }
    if (rumoured) {
      g.fillStyle = UI.brass;
      g.fillRect(mx + x * cw + 6, my + y * ch + 3, 4, 6);
    }
    /* ...AND WHETHER ANYTHING LIES THERE (2026-09-10, LS-302). Lighting a
     * brazier charts the ring of screens around it, and the card that asked
     * for these situations put it exactly: "the map is how this game already
     * says there is something over there". Charting said a screen EXISTS,
     * which a player could work out from the grid; it says whether the screen
     * holds something now, which is knowledge worth spending a charm on.
     * Charted only, not visited: it is the ember's gift, not a checklist of
     * what you have not picked up. */
    if (!seen && chartedHolds(game, x, y)) {   // ...ON A SCREEN YOU HAVE NOT WALKED (2026-09-18): the comment above and the docs both say so -- "it is the ember's gift, not a checklist of what you have not picked up" -- and the draw asked only whether the screen holds something, so a cache you had walked past was marked on every seed measured
      g.fillStyle = UI.gold;
      g.fillRect(mx + x * cw + cw - 6, my + y * ch + ch - 6, 2, 2);
    }
  }
  g.fillStyle = UI.paperDim;
  g.fillRect(mx + game.area.x * cw + 5, my + game.area.y * ch + 4, 4, 4);
  /* THE BODY, AND THE WAY (LS-397, 2026-09-12): the body's cell carries a breathing gold dot, and the needle from the dial
   * is drawn here too, from that cell toward the beast heard last, so the map answers "where am I" and "which way". */
  { const on = Math.sin(game.frame / 8) > -0.2; if (on) { g.fillStyle = UI.gold; g.fillRect(mx + game.area.x * cw + 6, my + game.area.y * ch + 5, 2, 2); }
    const last = game.needleBeast ? game.needleBeast() : null;
    const lair = last && game.world && game.world.lairs ? game.world.lairs.find((l: any) => l.kind === last.kind) : null;
    if (lair) { const x0 = mx + game.area.x * cw + 7, y0 = my + game.area.y * ch + 6, ddx = lair.x - game.area.x, ddy = lair.y - game.area.y, len = Math.hypot(ddx, ddy) || 1;
      g.fillStyle = UI.gold; for (let r = 4; r <= 14; r += 2) g.fillRect(Math.round(x0 + ddx / len * r), Math.round(y0 + ddy / len * r), 1, 1); } }
  /* THE BEASTS ON THE MAP (LS-225): a skull on the screen each one's record
   * stands on, dim while it lives and gold where it fell -- the quest is to
   * find them, and a map that will not say where is a map that wastes an
   * hour. */
  // rumours first and marks on top: with the lairs banded by distance a fallen beast's skull can sit inside a living
  // one's territory, and a tint painted over it dulled the gold (round 40)
  /* A RUMOUR THAT COVERS HALF THE ISLAND POINTS AT NOTHING (2026-09-06). At a radius of two each unseen beast
   * stained a five-by-five block of screens, and three of them cover a MEAN OF 51 CELLS OF 100 -- 65 in the worst
   * seed. Opening the map ten seconds into a run, most of the board was tinted, which tells a player only that the
   * beasts are somewhere in the wilds, which they already knew from the title screen. At a radius of one it is a
   * three-by-three block apiece, at most 27 cells and usually fewer, and the tint is a place you can walk to. */
  for (const b of (game.state.beasts || [])) {
    if (!(b.alive && !b.seen)) continue;
    g.fillStyle = 'rgba(210,94,72,0.13)';
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) { const x = b.home[0] + dx, y = b.home[1] + dy; if (x >= 0 && y >= 0 && x < WORLD_W && y < WORLD_H) g.fillRect(mx + x * cw, my + y * ch, cw - 2, ch - 2); }
  }
  for (const b of (game.state.beasts || [])) {
    const bx = Math.max(0, Math.min(WORLD_W - 1, Math.floor(b.x / PW))), by = Math.max(0, Math.min(WORLD_H - 1, Math.floor(b.y / PH)));
    if (b.alive && !b.seen) continue;   // a rumour, tinted above; no mark
    g.fillStyle = b.alive ? UI.mutedLit : UI.gold;
    g.fillRect(mx + bx * cw + cw - 8, my + by * ch + 2, 5, 4); g.fillRect(mx + bx * cw + cw - 7, my + by * ch + 6, 3, 1);
    g.fillStyle = UI.hud; g.fillRect(mx + bx * cw + cw - 7, my + by * ch + 4, 1, 1); g.fillRect(mx + bx * cw + cw - 5, my + by * ch + 4, 1, 1);
  }


  /* ROOM UNDER THE GRID FOR THE SOUTH BEARING.
   *
   * The touch-only seed line was drawn at `y - 12`, four pixels ABOVE the
   * grid's bottom edge, which is exactly where the compass word sits. It
   * fitted while the bearing was one glyph; round 31 spelled the whole word
   * and made it 2.3x wider WITHOUT re-checking this anchor, and the two marks
   * cut straight through the middle of a long seed name. Measured on canvas
   * pixels at 390x844: rows 176-178 carry both.
   *
   * `layout.mjs` cannot see this — it measures DOM boxes and never reads the
   * canvas — so the compass band is reserved here instead, and everything
   * below moves down with it. */
  /* the rows under the grid, from its bottom edge: the bearing owns the first nine, then the seed line on a phone,
   * then the legend, then the footer's own count -- and the last of them is inside the picture by construction */
  const gridBottom = my + WORLD_H * ch;
  const seedRow = gridBottom + 11;
  let y = Math.min(gridBottom + footRows - 8, HUD_H + PH - 9);
  if (game.touch) game.textMid('seed ' + game.seed, seedRow, UI.underPale, 8);
  game.text('beasts ' + (game.state.beastsSlain || []).length + '/3   glim ' + game.state.glim, 14, y, UI.paperDim, 8);
  y += 12;   // the charms are stacks in the hotbar now (LS-225); the kit line they had here ran off the bottom of a ten-row map
  /* THE MAP IS WHERE THE SCRIPT IS LEARNED.
   *
   * The standing stones say `suna <bearing>` — the sow's place lies that way — and
   * a lore reviewer scored `legibility` 5 for the obvious reason: nothing in
   * the game ever pairs a mark with its meaning. A winning run passes about
   * nine stones and meets a median of ONE word it does not already know. Four
   * glyphs, seen three times, out of a sixty-glyph alphabet. That is not a
   * script a player can learn; it is three logos.
   *
   * A Rosetta has to be somewhere a player already looks, and it has to pair
   * the mark with the thing rather than with a word. The map is that place:
   * the marks for the four bearings sit at the four edges of the world grid,
   * where north IS the top of the map, and `suna` sits beside the sow's lair the
   * player has found. Nothing is translated into English — the map's own
   * geometry does the teaching, which is the same rule the rest of the game
   * follows.
   *
   * This also replaces the line that used to read `links open cracked then
   * water`, which handed over in plain English exactly the kind of fact the
   * stones exist to carry. */
  const mid = mx + (WORLD_W * cw) / 2 - 4;
  const midY = my + (WORLD_H * ch) / 2 - 4;
  /** [word, x, y, drawDownward] — a tuple, so the numbers stay numbers.
   *  Without the annotation TS widens the rows to `(string|number|boolean)[]`
   *  and every coordinate becomes a union. */
  const compass: Array<[string, number, number, boolean]> = [
    // horizontal words are centred on the axis; the side pair is written
    // downward and centred on ITS axis instead
    [WORDS.north, mid - 6, my - 17, false], [WORDS.south, mid - 6, my + WORLD_H * ch + 1, false],   // ABOVE THE GRID'S OWN SLAB TOO (2026-09-18): at my-11 the word's nine rows always overlapped the slab's top four, whatever `my` was, so half its ink sat on bone at 1.26:1
    [WORDS.west, mx - 12, midY - 5, true], [WORDS.east, mx + WORLD_W * cw + 2, midY - 5, true],
  ];
  /* THE WHOLE WORD, NOT ITS FIRST MARK.
   *
   * This drew `syllablesOf(word)[0]` — one glyph per bearing — so the map
   * taught five marks while the stones show ten. A lore reviewer measured the
   * gap exactly: `ko la no ra si` appear only in the enlarged panel and are
   * taught NOWHERE, and three of those five are the more complex three-branch
   * figures. Worse than a gap, it was a contradiction: the key spelled
   * `hollow` with one mark and every inscription in the world spells it with
   * two, so leaning in to read a stone did not enlarge the writing, it
   * RESPELLED it.
   *
   * `drawWord`/`drawWordDown` already existed. The side bearings run downward
   * because a narrow gutter is what they have, and because that is how a
   * stone writes on its own two-tile shaft — the key and the object now agree
   * about direction as well as spelling. */
  for (const [word, gx, gy, down] of compass) {
    if (down) drawWordDown(g, word, Math.round(gx), Math.round(gy), 9, UI.stone);
    else drawWord(g, word, Math.round(gx), Math.round(gy), 9, UI.stone);
  }
  /* THE KEY (LS-225): two skulls on the beasts' own row, right of the count
   * -- a dim one for a beast that lives, a gold one for a beast that fell.
   * The hollow key that stood here ran off the bottom of a ten-row map. */
  const skull = (x: number, yy: number, col: string) => {
    g.fillStyle = col; g.fillRect(x, yy, 5, 4); g.fillRect(x + 1, yy + 4, 3, 1);
    g.fillStyle = UI.hud; g.fillRect(x + 1, yy + 2, 1, 1); g.fillRect(x + 3, yy + 2, 1, 1);
  };
  let kx = 150;
  const ky = y - 13;   // on the count's own row (2026-09-19): it sat two rows under 'beasts n/3', its bottom row on the frame line on a phone
  for (const [col, label] of [[UI.mutedLit, 'beast'], [UI.gold, 'fallen']] as Array<[string, string]>) {
    skull(kx, ky + 2, col); kx += 8;
    game.text(label, kx, ky + 1, UI.mutedLit, 8);
    kx += game.textWidth(label, 8) + 8;
  }
  // ...AND THE WORDS: a beast seen has its stone-word glossed beside the map, so the script is learned by meeting it
  const SHORT: Record<string, string> = { sow: 'sow', oldshell: 'shell', bramblemother: 'mother' };
  let gy = HUD_H + 30;   // under the title strip, from the same anchor the plate uses (2026-09-17)
  // in the script itself, with the English under it: a gloss in Latin letters could not be matched to the marks on a stone (the lore lens, round 40)
  for (const b of (game.state.beasts || [])) if ((b.seen || b.heard) && WORDS[b.kind]) { const col = b.alive ? UI.mutedLit : UI.gold; drawWord(g, WORDS[b.kind], glossX, gy, 9, col); game.text(b.seen ? (SHORT[b.kind] || b.kind) : '?', glossX, gy + 11, col, 8); gy += 22; }   // heard from a stone: the word without its English, until the beast is met   // clear of the compass pointer at x 182 (the art lens, round 42)
}

/** THE MAP'S OWN GEOMETRY, IN ONE PLACE (2026-09-17). The chart's cell height was measured against a flat 150 rows
 *  and everything written UNDER the grid -- the south bearing, the footer's count, the skull legend beside it, and on
 *  a phone the seed line -- was fitted into whatever was left, which at sixteen by thirteen screens was nothing: the
 *  seed line was drawn straight through the bearing, and through the legend. The rows below are reserved first and
 *  the grid takes the rest. Exported because a check that recomputes this by hand goes wrong the moment it changes. */
export function mapGeom(game: any) {
  /* THE COMPASS'S NORTH WORD HAS ITS OWN ROWS (2026-09-18). `footRows` has always reserved the rows BELOW the grid
   * for the south bearing and the footer; nothing reserved the rows above it, so `sura` -- north, the key to reading
   * every other bearing on the chart -- was written across the bone title plate: 27 of its 34 ink pixels sat under
   * 1.5:1 contrast while east and west read at 3:1 on the slate. Nine rows, the bearing's own height. */
  const my = HUD_H + 26 + 15;   // the bearing's nine rows and the clear ones either side of them
  const footRows = 23 + (game && game.touch ? 11 : 0);   // the bearing's nine rows, the footer's line and the legend's, and the seed line on a phone
  return { mx: 14, my, footRows,
           cw: Math.min(16, Math.floor(196 / WORLD_W)),
           ch: Math.max(6, Math.min(13, Math.floor((HUD_H + PH - my - footRows) / WORLD_H))) };
}

/* THE HORIZON IS BELOW THE WRITING. The treeline stood at y 150 while `continuing -- n/3 beasts slain`, the
 * records line and the long night's line are written at 154, 168 and 180 -- so on any save with a record all three
 * were printed ON the trees. Nothing showed it because a fresh seed has none of them. */
const HORIZON = 200;

/** The night over the island, drawn once for the screen you start on and the screen you finish on. */
function nightSky(game, g) {
  g.fillStyle = UI.ink; g.fillRect(0, 0, VW, VH);
  for (let i = 0; i < 70; i++) {
    const r = new RNG(mix(game.seedNum, i));
    const x = r.int(VW), y = r.int(HORIZON - 12);
    g.fillStyle = [UI.mapBloom, UI.violetLit, UI.mapSand][i % 3];
    g.fillRect(x, y, 1, 1);
    if (i % 7 === 0) { g.fillStyle = UI.cold; g.fillRect(x, y, 1, 1); }
  }
}

/** THE WOOD, ON BOTH SCREENS THAT SHOW IT. A run opens on this treeline and closes on it, so the ending is plainly
 *  the same country as the beginning rather than a page of text on black. */
function wood(game, g) {
  g.fillStyle = UI.deep;
  g.fillRect(0, HORIZON, VW, VH - HORIZON);
  const R = new RNG(mix(game.seedNum, 909));
  for (const rank of [0, 1]) {
    const base = HORIZON + (rank ? 4 : 0);
    let x = -10 + (rank ? 6 : 0);
    while (x < VW + 12) {
      const w = 9 + R.int(7), h = 7 + R.int(5), lift = R.int(7);
      const y = base - lift;
      g.fillStyle = rank ? UI.deeper : UI.tidepool;   // the far rank is nearer the sky's tone, so the near one reads in front of it
      const tw = w > 12 ? 3 : 2;
      g.fillRect(x + (w >> 1) - (tw >> 1), y + h - 2, tw, 5 + R.int(4));   // a stub under the canopy: at fourteen rows every trunk showed and the wood came out as a picket fence
      g.fillRect(x + 1, y + 2, w - 2, h - 2);
      g.fillRect(x + 2, y, w - 4, 3);
      g.fillRect(x + 3, y - 2, w - 6, 3);
      if (rank) { g.fillStyle = UI.tidepool; g.fillRect(x + 3, y + 3, Math.max(2, w - 8), 2); }
      x += w - 1 - R.int(3);
    }
  }
}

/** A name cut in the bar's own bone, centred, with its plate.
 *
 *  THE PLATE FITS THE PICTURE, NOT THE OTHER WAY ROUND. At a fixed size of twelve `the wilds are quiet again` is
 *  wider than the screen, so the plate ran off both edges and the name came out as `e wilds are quiet aga` -- the
 *  same fault as the toast and the bag's caption, made once more in a new place. The face steps down when the words
 *  need it, and the plate is clamped besides. */
function plaque(game, g, text: string, y: number, size = 12) {
  let face = size;
  while (face > 8 && game.textWidth(text, face) > VW - 44) face -= 4;
  const tw = game.textWidth(text, face), pw = Math.min(VW - 8, tw + 34);
  slab(g, Math.round((VW - pw) / 2), y, pw, face + 18, 'bone');
  game.text(text, Math.round((VW - tw) / 2), y + 10, BONE_INK, face);
}

/* THE TITLE SCREEN EXPLAINS NOTHING (2026-09-10, Vijay: "no instructions on the
 * title screen ... the game should explain nothing").
 *
 * What stood here was a plaque and four lines of prose -- what the beasts are,
 * what to do about them, and nine controls named in type dimmer than anything
 * else on the surface -- over a mostly empty black field. Two of those lines
 * told a player the whole shape of the game before they had seen one screen of
 * it. The rest of the game is built on the opposite rule, and
 * test/checks/manual.ts holds it: nothing is explained in words, and what a
 * player needs is taught by light. The one surface that broke the rule was the
 * first one they ever saw.
 *
 * What is here now is a place, at night, with the name of the game cut into a
 * stone standing in it. The three are on the ridge, far off and dark. The
 * stone carries the same word in the marks the world writes in -- a player who
 * has read a few standing stones can sound it out, and it names nothing they
 * will ever find. Nothing says what to press: the stone breathes and a mote
 * lifts off its head, which is how every other affordance in this game is
 * taught. The only words are the seed and the run's own numbers, and a seed is
 * a name and a record is a fact. */
/** WHETHER A CHARTED SCREEN HOLDS SOMETHING STILL LYING THERE (LS-302).
 *
 *  Exported because a rule drawn inside a paint loop is a rule no check can
 *  ask about, and this one is the ember charm's whole reward: lighting a
 *  brazier charts the ring of screens around it, and the mark is how the map
 *  says "there is something over there". Charted, not visited -- it is what
 *  the charm bought, not a list of what you have not picked up. */
export function chartedHolds(game: any, x: number, y: number) {
  if (!game.charted || !game.charted.has(x + ',' + y) || !game.worldLoot) return false;
  const taken = game.state.lootTaken || [];
  return game.worldLoot().some((l: any) => l.x === x && l.y === y
    && !taken.includes(l.x + ',' + l.y + ':' + l.kind));
}

export function drawTitle(game) {
  const g = game.g;
  nightSky(game, g);
  moon(game, g);
  ridges(game, g);
  wood(game, g);
  beastsOnTheRidge(game, g);
  markStone(game, g, 40, HORIZON - 50);
  /* THE TITLE'S STONE STANDS UNLIT (2026-09-20; Vijay: "the gold shimmer thing on stones looks terrible get rid of
   * it"). It breathed gold at its head with a spark lifting off it, the same cue the wilds put on a thing the key
   * acts on -- a twinkling ornament on the one still object of the card. The wordmark cut into the stone and the
   * three drifting motes are the card's own movement. */
  wordmark(game, g, 22);
  /* THREE MOTES, ON THEIR OWN CLOCKS. A still night reads as a still IMAGE;
   * this is the same drifting light the world uses over grass and water. */
  for (let i = 0; i < 3; i++) {
    const t = ((game.frame + i * 320) % 960) / 960;
    const mx = 60 + i * 66 + Math.round(Math.sin((game.frame + i * 90) / 64) * 7);
    g.fillStyle = mixHex('#3b465e', '#9fb6c8', 0.35 + 0.4 * Math.sin(t * Math.PI));
    g.fillRect(mx, Math.round(HORIZON - 26 - t * 74), 1, 1);
  }
  /* THE SECOND SPELLING IS ON THE STONE, not floating in the sky beside it.
   * A word in this world is a word CUT INTO something -- that is what a
   * standing stone is -- and two copies of it, one in the air, read as a logo
   * with a subtitle rather than a place with a name in it. */
  /* AND THE ONLY WORDS: which world this is, and what has been done in it.
   * Neither is an instruction -- a seed is a name and a record is a fact --
   * and both sit at the foot, out of the picture's way. */
  game.textMid('seed ' + game.seed, 232, UI.underPale, 8);
  // the felled count is gone from here (LS-379): a tally of three was the kill list on the title screen
  const rec = game.constructor.records();
  const mine = rec.seeds && rec.seeds[game.seed];
  /* THE ROW IS THE RUN THAT CHANGED THE MOST, SO SAY SO (2026-09-18). Round 52 changed what a seed's row keeps --
   * `recordWin` keeps the highest `changed`, and the win screen already reports it as "your most here before: N
   * changed" -- and this line was not moved with it: it printed that run's CLOCK under the word "best", so a better
   * run made the number go up (0:08 to 1:23) and a worse one made it go down. Same measure, same words, both ends. */
  if (mine) game.textMid('most here ' + (mine.changed || 0) + ' changed', 256, UI.mutedLit, 8);
  else if (rec.total && (rec.total.runs || rec.total.wins)) {
    // runs are runs; quieted is the wilds with all three beasts down (round 52: a leave said 'quieted' and a clock)
    game.textMid([rec.total.runs ? count(rec.total.runs, 'run') : '', rec.total.wins ? rec.total.wins + ' quieted' : ''].filter(Boolean).join('   '), 256, UI.mutedLit, 8);   // '0 runs   1 quieted' is not a sentence (2026-09-19)
  }
}

/** A disc, in pixels rather than an arc: the canvas would feather the edge and
 *  nothing else in this game has a soft edge. */
function pdisc(g, cx: number, cy: number, r: number) {
  for (let dy = -r; dy <= r; dy++) {
    const w = Math.floor(Math.sqrt(Math.max(0, r * r - dy * dy)));
    if (w > 0) g.fillRect(Math.round(cx - w), Math.round(cy + dy), w * 2, 1);
  }
}

/** THE MOON, WHICH IS WHERE THE LIGHT ON THIS SCREEN COMES FROM: the stone's
 *  lit face, the ridge tones and the wordmark's top edge are all off it. */
function moon(game, g) {
  const cx = VW - 40, cy = 78, r = 10;
  const halo = ['#2c344e', '#262c42', '#202538', '#1b1f2e', '#171a26'];
  for (let ring = halo.length; ring >= 1; ring--) { g.fillStyle = halo[ring - 1]; pdisc(g, cx, cy, r + ring * 3); }
  g.fillStyle = UI.foamLit; pdisc(g, cx, cy, r);
  g.fillStyle = '#cfe4ea'; pdisc(g, cx + 3, cy + 2, r - 3);
  g.fillStyle = '#b9d2da'; pdisc(g, cx + 5, cy + 4, r - 7);
}

/** Two ranks of hill behind the wood, so the night has depth in it rather than
 *  a treeline standing on nothing. The far rank is nearer the sky's own tone. */
function ridges(game, g) {
  for (const rank of [0, 1]) {
    const R = new RNG(mix(game.seedNum, 5150 + rank));
    const base = HORIZON - (rank ? 9 : 21);
    g.fillStyle = rank ? '#171b28' : '#12151f';
    let x = -6, h = 7 + R.int(6);
    while (x < VW + 8) {
      const w = 10 + R.int(16);
      h = Math.max(3, Math.min(19, h + R.int(7) - 3));
      g.fillRect(x, base - h, w, h + 28);
      x += w;
    }
  }
}

/** THE THREE, IN THE HUD: WHICH BEASTS, NOT HOW MANY (LS-397 b, 2026-09-15).
 *
 *  This row was three identical skulls lit by a COUNT -- `beastsSlain.length > i` -- so the one thing the whole run
 *  is for read as decoration, and nothing on the glass said there were three PARTICULAR creatures out there or
 *  which of them was still alive. The title screen had already refused the same idea in the same file: "not three
 *  skulls on a shelf: the beasts themselves". The HUD says it now too, in the same language and at a tenth of the
 *  size -- a hump with a snout, a ridged dome, a mass of bramble taller than it is wide -- each mark standing for
 *  its OWN beast and going gold when that one falls. A player who has felled the shell sees the shell go gold.
 *
 *  Order is the world's lair order, so a mark never moves between frames or between runs of a seed. */
function beastMarks(game, g, x0: number, y: number) {
  const lairs = (game.world && game.world.lairs) || [];
  const slain = new Set(game.state.beastsSlain || []);
  const kinds: string[] = lairs.length ? lairs.map((l: any) => l.kind) : ['sow', 'oldshell', 'bramblemother'];
  for (let i = 0; i < 3; i++) {
    const kind = kinds[i], x = x0 + i * 14, down = slain.has(kind);
    // felled beasts go gold; the ones still out there stay the bone row's own dark, so the eye finds what is left
    g.fillStyle = down ? UI.gold : '#6b5f4d';
    if (kind === 'oldshell') {                       // a ridged dome on a foot
      for (let r = 0; r < 3; r++) g.fillRect(x + 1 + r, y + 4 - r, 9 - r * 2, 2);
      g.fillRect(x, y + 6, 11, 2); g.fillRect(x + 2, y + 8, 7, 1);
    } else if (kind === 'bramblemother') {           // a mass taller than it is wide, with nubs
      g.fillRect(x + 3, y + 2, 6, 7);
      g.fillRect(x + 1, y + 4, 2, 2); g.fillRect(x + 9, y + 3, 2, 2); g.fillRect(x + 2, y, 2, 2); g.fillRect(x + 7, y + 1, 2, 2);
    } else {                                         // the sow: a hump with a snout and two legs
      g.fillRect(x + 1, y + 3, 8, 4); g.fillRect(x + 2, y + 1, 5, 2);
      g.fillRect(x + 9, y + 4, 2, 2);
      g.fillRect(x + 2, y + 7, 2, 2); g.fillRect(x + 6, y + 7, 2, 2);
    }
  }
}

/** THE THREE, ON THE RIDGE, DARK. Not three skulls on a shelf: the beasts
 *  themselves, as far off as the country is wide -- a hump with a snout, a
 *  ridged dome, and a mass of bramble taller than it is wide -- drawn in the
 *  ridge's own ink so they read as shape rather than as sprite. */
function beastsOnTheRidge(game, g) {
  /* ON THE SKYLINE, NOT AMONG THE TREES. At the wood's own height they were
   * three dark boxes inside a dark treeline -- shapes with nothing behind them
   * to be shapes against. They stand on the far ridge now, where the sky is
   * what they are cut out of. */
  const y = HORIZON - 23, ink = '#232c40';
  g.fillStyle = ink;
  { const x = 122;                                  // the sow
    g.fillRect(x, y - 6, 21, 7); g.fillRect(x + 3, y - 9, 13, 4);
    g.fillRect(x + 19, y - 5, 5, 3);
    g.fillRect(x + 3, y + 1, 3, 3); g.fillRect(x + 14, y + 1, 3, 3); }
  { const x = 166;                                  // the old shell
    for (let i = 0; i < 5; i++) g.fillRect(x + i, y - 4 - i, 19 - i * 2, 3);
    g.fillRect(x, y - 4, 19, 5); g.fillRect(x + 5, y + 1, 10, 2); }
  { const x = 208;                                  // the bramble mother
    const R = new RNG(mix(game.seedNum, 771));
    g.fillRect(x + 3, y - 12, 13, 13);
    for (let i = 0; i < 15; i++) g.fillRect(x + R.int(19), y - 14 - R.int(7), 2 + R.int(3), 2 + R.int(4)); }
}

/** THE STONE THE NAME IS CUT IN, and the only thing on this screen that moves.
 *  It breathes -- the lit face rises and falls over about four seconds -- and a
 *  mote lifts off its head. That is the whole "press to begin": this game
 *  teaches with light everywhere else, and the title is not an exception. */
function markStone(game, g, x: number, y: number) {
  const w = 21, h = 56;
  const breath = 0.5 + 0.5 * Math.sin(game.frame / 38);
  g.fillStyle = UI.footShadow; g.fillRect(x - 3, y + h - 2, w + 7, 4);
  /* IT IS CUT STONE, NOT A DOOR. A flat pale rectangle with a bright border is
   * what this was, and it read as a lit doorway standing in a field. Tapered
   * head, three tones, a speckled face, and the lit edge only where the moon
   * can reach it. */
  for (let row = 0; row < h; row++) {
    const taper = row < 5 ? 4 - row : 0;
    g.fillStyle = '#1c202b';
    g.fillRect(x + taper, y + row, w - taper * 2, 1);
  }
  for (let row = 1; row < h - 1; row++) {
    const taper = row < 5 ? 4 - row : 0;
    g.fillStyle = mixHex('#2b3140', '#3d4657', row / h);
    g.fillRect(x + 1 + taper, y + row, w - 2 - taper * 2, 1);
  }
  const R = new RNG(mix(game.seedNum, 6161));
  for (let i = 0; i < 26; i++) {
    g.fillStyle = i % 3 ? '#242936' : '#454c60';
    g.fillRect(x + 2 + R.int(w - 4), y + 5 + R.int(h - 8), 1, 1);
  }
  g.fillStyle = mixHex('#4a5365', '#93a6b8', 0.2 + 0.5 * breath);
  g.fillRect(x + w - 3, y + 5, 2, h - 7);                       // the moonlit edge
  g.fillStyle = mixHex('#5d6a7e', '#cfe4ea', 0.2 + 0.5 * breath);
  g.fillRect(x + 4, y + 1, w - 8, 1);
  drawWordDown(g, 'rako', x + 6, y + 11, 9, mixHex('#101320', '#060710', breath));
  { const lift = (game.frame % 96) / 96;
    if (lift < 0.8) {
      g.fillStyle = mixHex(UI.foamLit, '#2c344e', lift / 0.8);
      g.fillRect(x + 8 + Math.round(Math.sin(game.frame / 14) * 2), Math.round(y - 3 - lift * 18), 1, 1);
    } }
}

/** THE NAME, CUT AND LIT: four letters at four times the face, the one thing on
 *  this screen allowed to be large. The dark pass under it is the cut, the pale
 *  pass over it is the light off the moon, and the letters are spaced wider
 *  than the font sets them so four of them read as a mark and not as a word in
 *  a sentence. */
function wordmark(game, g, y: number) {
  const word = 'rako', scale = 4, gap = 7;
  const advance = 8 * scale + gap;
  const x0 = Math.round((VW - (word.length * advance - gap)) / 2);
  for (let i = 0; i < word.length; i++) {
    const x = x0 + i * advance;
    drawText(g, word[i], x + 2, y + 3, '#090b11', scale);
    drawText(g, word[i], x - 1, y - 1, '#fff7e2', scale);
    drawText(g, word[i], x, y, '#e6d9bd', scale);
  }
}

/* THE RUN ENDS WHERE IT BEGAN. This was the plainest surface in the game -- a stack of centred lines on flat black
 * with the bottom third of the picture empty under them -- and it is the payoff for finishing. It stands on the same
 * night and the same treeline the title screen opens on, so the ending reads as the same country gone quiet, and the
 * run's numbers are cut into a plate rather than floating. */
/** "1 thing", "2 things", "1 cache", "2 caches": the record screen's first
 *  real run read "1 things   1 runs" (LS-361, 2026-09-11). */
export function count(n: number, one: string, many?: string) {
  return n + ' ' + (n === 1 ? one : many || (one.endsWith('ch') || one.endsWith('s') ? one + 'es' : one + 's'));
}
/** THE STONE UNDER OR BESIDE THE BODY, in the screen's own tiles (round 52's
 *  lore lens, 2026-09-11). The lean-in panel that shows a stone's whole
 *  inscription divided the player's WORLD position by the tile size and
 *  looked the result up in a map keyed by the screen's own tiles: it could
 *  show on 0 of 162 stones across four seeds. The player stands in the
 *  world (LS-117); the origin comes off first. */
export function stoneUnderfoot(game): { reading: any; at: [number, number] | null; sx?: number; sy?: number } {
  if (!(game.area && game.world && game.world.stones)) return { reading: null, at: null };
  const px2 = Math.floor(game.player.cx / TS), py2 = Math.floor(game.player.cy / TS);
  for (const [dx, dy] of ([[0, 0], [0, -1], [0, 1], [-1, 0], [1, 0], [0, -2]]) as Array<[number, number]>) {
    const wx = px2 + dx, wy = py2 + dy, charm = stoneAtWorld(game, wx, wy);
    if (charm) return { reading: charm, at: [wx, wy], sx: Math.floor(wx / COLS), sy: Math.floor(wy / ROWS) };
  }
  return { reading: null, at: null };
}
function stoneAtWorld(game, wx: number, wy: number) { return game.stoneAtWorld(wx, wy); }   // `at` is a world tile (LS-511): a stone over the seam reads from either side

export function drawWin(game) {
  /* THE END SCREEN IS A RECORD, NOT A VERDICT (LS-379, slice four). It said
   * "the wilds are quiet again" over three skulls, which is the kill list
   * congratulating you. A run ends when you leave by a stone, whenever that
   * is, and what the screen says is what you changed. */
  const g = game.g;
  nightSky(game, g);
  wood(game, g);
  plaque(game, g, 'you leave the wilds', 22);
  const r = game.runRecord ? game.runRecord() : { days: 0, felled: 0, setDown: 0, burned: 0, caches: 0, kills: 0 };
  const rows = [
    'seed ' + game.seed + '   ' + count(r.days, 'day'),
    count(r.felled, 'beast') + ' felled   ' + count(r.kills, 'thing'),
    r.setDown + ' set down   ' + r.burned + ' taken',
    count(r.caches, 'cache') + ' found',
  ];
  // in the pale, not the ink: the rows sit on the night sky now, where ink is invisible
  rows.forEach((t, i) => game.textMid(t, 76 + i * 12, UI.underPale, 8));   // textMid trims a row that is wider than the glass (2026-09-17: the seed row ran off both edges on a long seed)
  { const best = game.previousBest != null ? 'your most here before: ' + game.previousBest + ' changed' : 'your first time on this seed';
    game.textMid(best, 136, UI.glass, 8); }
  { const tot = game.constructor.records().total || {};
    if (tot.runs) game.textMid(count(tot.runs, 'run') + '   most changed ' + (tot.most || 0), 158, UI.underPale, 8); }
  game.textMid(game.touch ? 'tap anywhere for a new world' : 'enter for a new world', 232, UI.underPale, 8);
}

export function drawGameOver(game) {
  const g = game.g;
  g.fillStyle = 'rgba(14,12,18,0.85)';
  g.fillRect(0, HUD_H, VW, PH);
  game.textMid('the wilds take you', 108, UI.danger, 12);
  // THE SCREEN THAT ANNOUNCES THE COST WAS DENYING IT (the lore lens, round 50): dying with sixteen glim wakes you
  // with ten, and the very next line a player reads is `you wake - 6 glim lost`.
  /* ...AND IT SAID IT IN A CHARACTER THE FONT HAS NOT GOT. The em dash came out as `?`, and the line measures 330
   * pixels in a 256-pixel picture, so it ran off both edges as well. Wrapped, and punctuated in the alphabet the
   * game actually owns. */
  // (the rule that stood here in words -- felled stays felled, the bag pays -- is the game's to show, not to say: it was the one screen that told a rule, LS-397)
  /* ...AND THE WAY OUT IS NAMED (the mobile lens, round 50). The title screen says `tap anywhere to begin` on touch
   * and `press enter` on a desk; the death screen said neither, while the three biggest buttons under the thumb sat
   * lit, named and inert. What actually works on a phone is a tap anywhere on the picture. */
  /* ONE PROMPT, ON ONE ROW, AND IT DOES NOT BLINK. Round 50 added `press enter` to name the way out, at y 152 --
   * where a blinking `enter to return` was already being written. Both were drawn, both centred, one over the other,
   * so for half of every second the line telling a dead player how to carry on read `enpeestoenbborn`. Deleting the
   * older one, I kept its BLINK, and the mobile suite caught that within the hour: round 50's whole point was that
   * the way out is NAMED on this screen, and a line absent half the time is half a name. The title screen's prompt
   * blinks because there it is an invitation; here it is the only instruction a dead player has. */
  game.textMid(game.touch ? 'tap anywhere to play again' : 'press enter', 158, UI.paperDim, 8);
}

/** Put the player back after a death, at the door of the room they fell in
 *  rather than at the mouth of the hollow. `diedIn` and `cp` are read before
 *  the save is loaded, because loading it resets both. */

/** One slot of the bag: a frame, the item's glyph, its count. */
/* A STACK'S COUNT IN A THREE-BY-FIVE HAND: the eight-pixel face is the
 * smallest the game has, and a "23" in it hides the thing it counts (the
 * icons through the camera left nothing of the glim under the number). Three
 * by five with a one-pixel dark backing is what every inventory game does,
 * and at a glance it reads as a count and not as a caption. Right-aligned to
 * `xr`, its top at `y`. */
const TINY: Record<string, number[]> = {
  '0': [7, 5, 5, 5, 7], '1': [2, 6, 2, 2, 7], '2': [7, 1, 7, 4, 7], '3': [7, 1, 7, 1, 7], '4': [5, 5, 7, 1, 1],
  '5': [7, 4, 7, 1, 7], '6': [7, 4, 7, 5, 7], '7': [7, 1, 1, 1, 1], '8': [7, 5, 7, 5, 7], '9': [7, 5, 7, 1, 7],
};
export function tinyNum(g, n: number, xr: number, y: number, col: string, back?: string) {
  const t = String(n), w = t.length * 4 - 1, x0 = xr - w + 1;
  if (back) { g.fillStyle = back; g.fillRect(x0 - 1, y - 1, w + 2, 7); }
  g.fillStyle = col;
  for (let i = 0; i < t.length; i++) {
    const rows = TINY[t[i]]; if (!rows) continue;
    for (let r = 0; r < 5; r++) for (let c = 0; c < 3; c++) if (rows[r] & (4 >> c)) g.fillRect(x0 + i * 4 + c, y + r, 1, 1);
  }
}

/** THE STATUS ROW (LS-240). One row under the weapon's caption, left-aligned with the hotbar, holding ONE message:
 *  what ails you if anything does, else how many blows are left in the blade. Two writers on one row overprinted each
 *  other -- 'chilled' landed inside '90 of 90 blows' -- and the rule that decides it is that a wound outranks an edge. */
/** A definite unfinished recipe shows its missing piece and payoff, not another instruction. */
export function drawForgeGoal(game, forge) {
  if (game.bagOpen || activeNames(game.player).length) return false;
  const out = forge?.recipe?.out || forge?.missing?.out;
  if (!out || !ITEMS[out]) return false;
  const g = game.g;
  centered(g, fitCaption(game, ITEMS[out].name, 45), 7, 35, 46, BONE_INK);
  if (forge.missing) {
    drawWeapon(g, forge.missing.kind, 8, 47, false);
    if (forge.missing.n > 1) tinyNum(g, forge.missing.n, 21, 59, BONE_INK, BONE);
    g.fillStyle = BONE_INK;
    g.fillRect(26, 54, 6, 1);
    g.fillRect(29, 52, 1, 5); g.fillRect(30, 53, 1, 3);
    drawWeapon(g, out, 36, 47, false);
  } else drawWeapon(g, out, 23, 47, false);
  return true;
}

export function statusRow(game, forge = game.forgePreview?.()) {
  const names = activeNames(game.player);
  if (names.length) {
    const t = (names.length > 2 ? names.slice(0, 2).join(' ') + ' +' + (names.length - 2) : names.join(' ')).toLowerCase();
    /* IT SAID `SCORCHED` (2026-09-10). The line was lowered and then shouted
     * again in the same expression -- `.toLowerCase()` followed by
     * `.toUpperCase()` -- so the one red word on the glass was the one thing
     * still in capitals. */
    /* ...AND IT FLASHES ON THE FRAME THE POINT IS TAKEN, in the same colour
     * as the leaf that goes. Cause and effect were a full six seconds apart
     * and in two different corners of the HUD; a player who sees the word and
     * the vigor light up together has been told what is happening without a
     * line of text. */
    const flash = game.player && game.player.ailFlash && game.player.ailFlash.t > 0;
    game.text(fitCaption(game, t, 133), 64, 62, flash ? '#ff9273' : UI.danger, 8);   // the same red, brighter: two lights of one colour on one frame read as one event
    return t;
  }
  if (forge) {
    const caption = forge.recipe ? 'ready: ' + ITEMS[forge.recipe.out].name
      : forge.missing ? 'short: ' + (forge.missing.n > 1 ? forge.missing.n + ' ' : '') + ITEMS[forge.missing.kind].name
      : 'forge: incomplete';
    game.text(fitCaption(game, caption, 133), 64, 62, forge.recipe ? UI.gold : BONE, 8);
    return caption;
  }
  const sel = bagOf(game.state).slots[bagOf(game.state).selected];
  const wear = stackWear(sel || null);
  const worn = !!(sel && sel.dur !== undefined && ITEMS[sel.kind] && ITEMS[sel.kind].dur !== undefined && sel.dur < ITEMS[sel.kind].dur);   // the blow count appears once a blow has worn it (LS-397)
  if (wear && worn) game.text(fitCaption(game, wear, 133), 64, 62, sel && sel.dur !== undefined && sel.dur * 3 < (ITEMS[sel.kind].dur || 0) ? '#e0745c' : BONE, 8);
  else if (sel) game.text(fitCaption(game, ITEMS[sel.kind].name, 133), 64, 62, BONE, 8);
  return wear;
}

/* WHAT CASTS (the light is up and to the left) and WHAT TAKES a shadow: only flat, walkable ground, so a shadow never
 * lands on a cliff face or on water and reads as dirt. */
/* ONLY WHAT IS TALLER THAN A BODY throws a shadow across the ground: a cliff, a tree, a hedge, a nest. A boulder, a
 * cairn and a brazier are objects standing in a tile and carry their own contact shadow from the one camera already;
 * given a cast shadow as well, a scattered field of them threw a scattered field of violet squares. */
export const CASTS = new Set([T.CLIFF]);
/* HOW MUCH OF A TILE A THING FILLS. A cliff or a tree is the whole of it and throws the whole of it; a boulder, a
 * cairn, a nest or a brazier is a thing standing IN a tile, so its shadow is a smaller shape inside one -- drawn full
 * size, a scattering of rocks threw a scattering of squares, which is what the ground of the dust country looked like
 * for one round. Rubble and litter throw nothing at all: they lie flat. */
export const CAST_INSET: Record<number, number> = { [T.CLIFF]: 0 };   // a cliff is stencilled from its own painted mass; nothing else casts a tile-shaped shadow
export const SHADOW_ON = new Set([T.GROUND, T.MOSS, T.SAND, T.SCORCH, T.TUFT, T.DEBRIS, T.RAMP]);

/** The tones the HUD's panels are cut from: a lit edge where the light lands and a deep one in the shade. They are
 *  the whole of the reference renders' "material", and they are used by the bar, the bag's plate and every slot. */
export const PLATE_LIT = '#514b5c', PLATE_DEEP = '#100e17', WELL_FLOOR = '#24202b', PLATE_BODY = '#25232d';
/** The knotwork woven into the hotbar's ground, one step off it (the judge, 2026-09-05, with the tile it wanted). */
export const PLATE_KNOT = '#353240';
/** One step off the field's own dark: the knotwork woven into the hotbar's ground (the judge, 2026-09-05). */
/* THE PANEL'S OWN EDGES, and they are brighter than the lips inside it. The judge scored the first cut 4 of 10 --
 * "flat, and lacking the sense of physical material" -- and named the tones: a light grey along the top and left
 * where the light lands, a cool dark along the bottom and right, and a chiselled gold on a picked slot rather than
 * a yellow line. A panel edge has to be lighter than anything it contains or it does not read as an edge. */
export const EDGE_LIT = '#9494a4', EDGE_SHADE = '#33303f', PIP_SOCKET = '#201d2b';
/* CARVED BONE, which is what both bold redesigns reached for: a world of standing stones and great beasts frames its
 * readouts in the thing it kills. Three tones -- the cut face, the shaded side, and the ink in the carving. */
/* THE BONE'S OWN FOUR TONES, taken from the judge's reading of the target (2026-09-05, when it was finally asked to
 * name pixels and hex values rather than adjectives): a warm highlight, a mid that is greyer and warmer than ours
 * was, a shadow with red in it, and a near-black outline that is brown rather than violet. It called the swap "the
 * most significant visual lift" of the three, and it is one line. */
export const BONE = '#e6d6b8', BONE_LIT = '#f6ecd6', BONE_DIM = '#b19b7f', BONE_DARK = '#5c4b43', BONE_INK = '#322725';

/* THE FRAME IS CARVED THROUGH THE ONE CAMERA (2026-09-05; Vijay: "use the 3d pipeline", after 6 of 10 on a bar whose
 * mouldings were hand-placed rectangles). Every other solid in this game -- the stairs, the creatures, the bag's
 * icons, the boulders -- is built as three-dimensional shapes and lit by the same sun; the HUD's bone was the last
 * thing drawn as flat rects with a light line painted along one edge, which is exactly what a judge means by "no
 * material depth". A rod is a round moulding, a ball is the boss where two mouldings meet, and the renderer gives
 * each of them its lit face, its shaded underside and its own outline for nothing.
 *
 * ...AND IT IS ONE FRAME, NOT FIVE. A moulding round each readout reads as five plaques laid side by side; the
 * reference is one carved object with the fields cut into it. An outer moulding round the whole band, a rail between
 * the two rows, two uprights dividing the top row, and a boss wherever two mouldings meet -- which is what a carving
 * does at a junction and what a drawn rectangle cannot.
 *
 * Baked once: the frame depends on the layout, and the layout does not move. */
const BONE_SKIN = { base: BONE, lit: BONE_LIT, dark: BONE_DARK };
/** The four spaces the bar is divided into, in screen pixels: the FIELD each readout sits in, not its frame. The
 *  fields are painted from this table and the frame is built around it, so the two cannot drift apart. */
export const HUD_WELLS: Array<[number, number, number, number]> = [
  [7, 7, 46, 20], [63, 22, 135, 35], [207, 7, 42, 51], [64, 62, 134, 8],
];
let hudFrameSheet: any = null;
function hudFrame() {
  if (hudFrameSheet) return hudFrameSheet;
  const up = (y: number) => (HUD_H - 1) - y;   // the scene counts up from the band's foot
  const out: any[] = [];
  const rod = (x0: number, y0: number, x1: number, y1: number, r = 1.5) =>
    out.push({ kind: 'rod', a: [x0, up(y0), 0], b: [x1, up(y1), 0], r, skin: BONE_SKIN });
  const boss = (x: number, y: number, r = 2.5) =>
    out.push({ kind: 'ball', at: [x, up(y), 1.6], r, ry: r * 0.86, rz: r * 0.8, skin: BONE_SKIN });
  const L = 2, R = VW - 3, T = 2, B = HUD_H - 1;
  rod(L, T, 95, T, 1); rod(166, T, R, T, 1); rod(L, B, R, B, 1);
  rod(L, T, L, B, 1); rod(R, T, R, B, 1);
  for (const [bx, by] of [[L, T], [R, T], [L, B], [R, B], [58, T], [58, B], [201, T], [201, B]] as Array<[number, number]>) boss(bx, by, 2);
  // a smaller boss along each long run, so a moulding 250 pixels wide is a carving and not a pipe
  for (const bx of [30, 180]) boss(bx, T, 1.2);
  hudFrameSheet = M3.renderScene(VW, HUD_H, out, 0, HUD_H - 1, BONE_INK);
  return hudFrameSheet;
}

/* A CARVED CORNER, drawn a pixel at a time (the judge, 2026-09-05, once it was asked to name pixels: "replace the
 * two-tone bevel with a four-tone CURVED shape"). The solid renderer gives a round boss where two mouldings cross,
 * but a corner ornament is a quarter-round bracket that thins as it runs away from the corner, and that is a shape
 * no capsule makes. Seven by seven, four tones, mirrored into all four corners. */
const CORNER = [
  '....ooo',
  '..ooLLo',
  '.oLLLBo',
  '.oLLBDo',
  'oLLBD..',
  'oLBD...',
  'oLD....',
];
const CORNER_INK: Record<string, string> = { o: BONE_INK, L: BONE_LIT, B: BONE, D: BONE_DIM };

/** The carved frame, over everything the band has drawn: the readouts sit IN it, so it goes on last. */
export function drawHudFrame(game) {
  const g = game.g;
  M3.blitRuns(hudFrame(), (x: number, y: number, w: number, c: string) => { g.fillStyle = c; g.fillRect(x, y, w, 1); });
  for (const [ox, oy, fx, fy] of [[1, 1, 1, 1], [VW - 2, 1, -1, 1], [1, HUD_H - 2, 1, -1], [VW - 2, HUD_H - 2, -1, -1]] as Array<[number, number, number, number]>) {
    for (let ry = 0; ry < CORNER.length; ry++) for (let rx = 0; rx < CORNER[ry].length; rx++) {
      const ch = CORNER[ry][rx]; if (ch === '.') continue;
      g.fillStyle = CORNER_INK[ch];
      g.fillRect(ox + rx * fx, oy + ry * fy, 1, 1);
    }
  }
}
/** The ink cut into the bone: what the two rows of words on the plate are written in. */
export const BONE_INK_DIM = '#6a6152';
export const GOLD_LIT = '#ffcc66', GOLD_DEEP = '#b4752b';

export function drawSlot(game, g, x, y, stack, lit, cursor = false) {
  /* A SLOT IS A WELL CUT INTO THE PLATE (2026-09-05, the reference HUD renders). It was a flat black square with a
   * gold bar over and under the picked one; every reference drew an inset socket -- floor, a shaded top-left, a lit
   * bottom-right -- and framed the picked one in gold all the way round. Four rects more, and the strip reads as
   * nine sockets rather than one black band with icons on it. */
  g.fillStyle = lit ? UI.shadowHard : WELL_FLOOR; g.fillRect(x, y, 14, 15);
  g.fillStyle = PLATE_DEEP; g.fillRect(x, y, 14, 1); g.fillRect(x, y, 1, 15);          // the shaded lip, up and left: the well is INSET, so its top is the dark one
  g.fillStyle = game.bagOpen ? '#827b85' : '#514a59'; g.fillRect(x, y + 14, 14, 1); g.fillRect(x + 13, y, 1, 15);
  /* A PICKED SLOT IS CHISELLED GOLD, not a yellow line (the judge, 2026-09-05): bright along the top and the left,
   * deep along the bottom and the right, so the frame has metal in it and the eye finds it without looking. It is
   * painted AFTER the icon -- drawn first, a sickle's blade ran over the right-hand column and broke the frame. */
  const frame = () => {
    if (!lit) return;
    /* TWO PIXELS OF GOLD, not one (the judge, 2026-09-05, and both bold redesigns): at one pixel the picked slot
     * reads as a line drawn round a square; at two it reads as a mount the icon sits in. */
    g.fillStyle = GOLD_LIT; g.fillRect(x, y, 14, 2); g.fillRect(x, y, 2, 15);
    g.fillStyle = GOLD_DEEP; g.fillRect(x, y + 13, 14, 2); g.fillRect(x + 12, y, 2, 15);
    g.fillStyle = UI.gold; g.fillRect(x + 1, y + 1, 12, 1); g.fillRect(x + 1, y + 1, 1, 13);
    // ...and a bright pip at each corner of the mount, which is what makes it ornament rather than a border
    g.fillStyle = GOLD_LIT;
    for (const [px2, py2] of [[x, y], [x + 12, y], [x, y + 13], [x + 12, y + 13]] as Array<[number, number]>) g.fillRect(px2, py2, 2, 2);
  };
  if (!stack) frame();
  if (cursor) { g.fillStyle = UI.paperDim; g.fillRect(x - 1, y - 1, 16, 2); g.fillRect(x - 1, y + 14, 16, 2); g.fillRect(x - 1, y - 1, 2, 17); g.fillRect(x + 13, y - 1, 2, 17); }   // two pixels: one was a hair on a phone (LS-234)
  if (!stack) return;
  const k = stack.kind;
  if (k === 'sickle' || k === 'spear' || k === 'axe' || k === 'torch') drawWeapon(g, k, x + 2, y + 1, true);
  else drawWeapon(g, k, x + 1, y + 1, true);
  if (stack.n > 1) tinyNum(g, stack.n, x + 13, y + 9, UI.paper, UI.slotEmpty);
  // the wear bar (LS-238): shown once a blade has been used, red under a third
  const mx = ITEMS[k] && ITEMS[k].dur;
  if (mx && stack.dur !== undefined && stack.dur < mx) { g.fillStyle = UI.ink; g.fillRect(x + 1, y + 13, 12, 1); g.fillStyle = stack.dur * 3 < mx ? '#d0523c' : UI.gold; g.fillRect(x + 1, y + 13, Math.max(stack.dur > 0 ? 1 : 0, Math.round(12 * stack.dur / mx)), 1); }
  frame();
}
export function drawPickupCue(game, g, x, y, stack) {
  const left = stack ? (game.pickupUntil?.get(stack) || 0) - game.runFrames : 0;
  if (left <= 0) return;
  // Cool corner marks distinguish an arrival from the gold selected-slot frame.
  g.save();
  g.globalAlpha = Math.min(1, left / 30);
  g.fillStyle = '#a8f0df';
  for (const [dx, dy] of [[0, 0], [10, 0], [0, 13], [10, 13]]) g.fillRect(x + dx, y + dy, 4, 2);
  for (const dx of [0, 12]) { g.fillRect(x + dx, y + 2, 2, 2); g.fillRect(x + dx, y + 11, 2, 2); }
  g.restore();
}
export function drawHotbar(game, g, x0, y0, caption = true) {
  const b = bagOf(game.state);
  /* THE STRIP IS A PICTURE UNTIL THE BAG CHANGES (LS-227): nine icons through
   * the pipeline were two hundred rects a frame on a phone. Painted once per
   * signature (kinds, counts, the picked slot) into a small canvas, then one
   * drawImage. */
  const sig = b.slots.slice(0, HOTBAR).map((s) => s ? s.kind + ':' + s.n + ':' + (s.dur === undefined ? '' : s.dur) : '-').join(',') + '|' + b.selected + '|' + (game.bagOpen ? 1 : 0);   // the wear bar is part of the picture (LS-238), and so is the bag being open (2026-09-18): drawSlot lightens every lit lip while it is, and the second row is drawn straight every frame, so a stale strip showed the bar in two colours at once
  if (typeof document !== 'undefined') {
    let hc = game.hotbarCache;
    if (!hc) { const canvas = document.createElement('canvas'); canvas.width = HOTBAR * 15; canvas.height = 15; hc = game.hotbarCache = { sig: '', canvas }; }
    if (hc.sig !== sig) { const cg = hc.canvas.getContext('2d'); cg.clearRect(0, 0, hc.canvas.width, 15); const save = game.g; game.g = cg; for (let i = 0; i < HOTBAR; i++) drawSlot(game, cg, i * 15, 0, b.slots[i], i === b.selected); game.g = save; hc.sig = sig; }
    g.drawImage(hc.canvas, x0, y0);
  } else for (let i = 0; i < HOTBAR; i++) drawSlot(game, g, x0 + i * 15, y0, b.slots[i], i === b.selected);
  // Animated feedback stays outside the cached icon strip.
  for (let i = 0; i < HOTBAR; i++) drawPickupCue(game, g, x0 + i * 15, y0, b.slots[i]);
  if (!caption) return;
  const sel = b.slots[b.selected];
  /* THE CAPTION STOPS AT THE CLOCK (the mobile lens, round 48): a worn piece's line ran 383 pixels in a 256-pixel
   * picture and overprinted the run's time. The short form first, then trimmed to what is left of the row. */
  { const budget = VW - 8 - game.textWidth('0:00', 8) - x0 - 6;
    let cap = stackCaption(sel || null);
    if (game.textWidth(cap, 8) > budget) cap = stackCaption(sel || null, true);
    /* THE READOUTS ARE CUT IN CAPITALS. Both bold redesigns set every label on the plate in caps -- VIGOR, SICKLE,
     * POD 4, WILDS / FEN -- which is what a carved thing looks like, and it separates the bar's labels from the
     * game's own lowercase voice in the toasts and the screens. The prose is untouched. */
    /* THE NAME IS THE HEADLINE AND THE NUMBERS ARE NOT (the judge, 2026-09-05: "use a stylized all-caps face for the
     * primary information and a smaller, simpler one for the secondary stats"). One row, two weights: the weapon's
     * name in the frame's own bone, its verb, point and reach a step down in the muted tone. */
    { const line = fitCaption(game, cap, budget);
      const sp = line.indexOf('  ');
      const head = sp > 0 ? line.slice(0, sp) : line, rest = sp > 0 ? line.slice(sp) : '';
      game.text(head, x0, y0 + 23, sel ? BONE_LIT : UI.mutedLit, 8);
      if (rest) game.text(rest, x0 + game.textWidth(head, 8), y0 + 23, UI.mutedLit, 8); } }   // the numbers, not just the name (LS-238): verb, point, reach; bare hands when nothing is picked
  // the blows left are written by statusRow, on the row under this one: drawn here they were overprinted by whatever ails you (LS-240)
}
/** A sentence broken at spaces to fit a width, at most `max` lines; the last is cut with an ellipsis if the words
 *  run past it. Written once because the picture is 256 pixels wide and three separate places had each decided for
 *  themselves what to do about that -- two of them by doing nothing. */
export function wrapLines(game, text: string, width: number, size = 8, max = 2): string[] {
  const out: string[] = [];
  for (const word of String(text).split(' ')) {
    const joined = out.length ? out[out.length - 1] + ' ' + word : word;
    if (out.length && game.textWidth(joined, size) <= width) out[out.length - 1] = joined;
    else out.push(word);
  }
  while (out.length > max) out[max - 1] = out[max - 1] + ' ' + out.splice(max, 1)[0];
  if (out.length) out[out.length - 1] = fitCaption(game, out[out.length - 1], width);
  return out;
}

/** A caption cut to the room it has, at a word where a word will do and with an
 *  ellipsis to say it was cut. Exported so a check can measure what is painted
 *  rather than what was asked for (the mobile lens, round 49). */
export function fitCaption(game, text: string, budget: number): string {
  if (!text || game.textWidth(text, 8) <= budget) return text || '';
  const dots = game.textWidth('…', 8);
  let cut = text;
  while (cut.length > 1 && game.textWidth(cut, 8) + dots > budget) {
    const sp = cut.lastIndexOf(' ');
    cut = sp > 3 ? cut.slice(0, sp) : cut.slice(0, -1);   // at a word while there is one to cut at, then a letter at a time
  }
  return cut.replace(/[\s:,-]+$/, '') + '…';
}

/** Match wrapping to the native 3x5 caption face, not the wider world-text font. */
export function bagEffectLines(stack, touch = false): string[] {
  const def = stack && ITEMS[stack.kind];
  const effect = def?.gear?.note || def?.note;
  return effect ? wrapLines({ textWidth: (text) => text.length * 4 - 1 }, effect, touch ? 240 : 208) : [];
}

/** The bag screen: every slot, the cursor, the lifted stack, over a dimmed world. */
export function drawBag(game) {
  const g = game.g, b = bagOf(game.state);
  if (!game.inkUiOn) { g.fillStyle = 'rgba(10,8,16,0.72)'; g.fillRect(0, HUD_H, VW, 192); }   // the inked bag lays its own warm wash (inkui.ts)
  /* AND THE GRID SITS ON A PLATE, not on the world (the art lens, round 48): through a 72% wash the trees and the water
   * stayed legible between the slots, and 'feet' and 'charm' were written across a lake. */
  /* THE BAG IS THE BAR'S OWN MATERIAL (2026-09-05; Vijay, of the rebuilt bar: "codex did a great job can we do
   * similar"). The bar became carved bone with the readouts cut into it and the bag stayed grey stone, so opening it
   * changed the game's furniture -- and its worn column was three plates floating beside the grid with their labels
   * written across the gap between them. One panel now, in bone, wide enough to hold the grid AND the worn column,
   * with the labels cut small under their own boxes where nothing can run into them. */
  { const p = bagPitch(!!game.touch, game.cssScale, b.cursor);
    const boxes = loadoutBoxes(!!game.touch);
    /* The panel holds BOTH the grid and the worn column, so its height is whichever reaches lower -- the column's
     * last box hangs 26 rows below its own top and used to fall straight off the plate. */
    const px0 = Math.max(0, p.x0 - 8), pw = Math.min(VW - px0 - 2, (game.touch ? p.cols * p.px + 16 : (boxes[1][1] + 23) - px0 + 4));
    const gridBottom = p.y0 + p.rows * p.py + (game.touch ? 0 : 6);
    const colBottom = game.touch ? 0 : Math.max(...boxes.filter((bx2) => bx2[0] !== 'hand').map((bx2) => bx2[2])) + 32;   // the column's last plate, and no lower: the panel must not close over the line that says how to work the bag
    slab(g, px0, p.y0 - 9, pw, Math.max(gridBottom, colBottom) - (p.y0 - 9), 'bone', '#241f2b');
    for (const [slot, x, y] of boxes) {
      /* THE PLATE CARRIES ITS OWN WELL, shallower than slab()'s. slab's inset stops five rows above the plate's foot,
       * which is exactly where the name goes, so every label was cut across the bottom bevel and onto the plate below
       * it. The well is drawn to the icon instead, and the name sits on bare bone under it, in ink. */
      /* ...AND THE HAND BOX GETS ONE TOO WHEREVER THE STRIP DOES NOT COVER IT. On a desk it is drawn into the full
       * width of the title strip, which is its plate; on a phone it stands at x 22 well below the strip, and it was
       * the one box in the loadout row left sitting on the open world. */
      if (slot !== 'hand' || game.touch) {
        slab(g, x - 7, y - 4, 28, 32, 'bone');
        g.fillStyle = WELL_FLOOR; g.fillRect(x - 2, y - 2, 19, 19);
        g.fillStyle = MATERIAL.bone[1]; g.fillRect(x - 2, y - 2, 19, 1); g.fillRect(x - 2, y - 2, 1, 19);
      }
    }
  }
  const P = bagPitch(!!game.touch, game.cssScale, b.cursor), cols = P.cols, rows = P.rows, x0 = P.x0, y0 = P.y0;
  /* THE STRIP IS THE FULL WIDTH OF THE PICTURE, because the hand box lives at x 226 and is drawn INTO it. Centred on
   * the grid it ran 53..203, so the picked weapon's box floated on the dimmed world with nothing under it and the
   * cursor's caption was cut off mid-letter at the strip's edge. */
  slab(g, 2, HUD_H + 1, VW - 4, 28, 'bone');
  smallText(g, game.touch ? `pack ${P.page + 1}/${P.pages}` : 'the pack', 8, HUD_H + 5, BONE_INK, 52);
  for (let i = P.offset; i < Math.min(BAG_SLOTS, P.offset + P.count); i++) {
    const local = i - P.offset;
    const zoom = game.touch ? 2 : 1;
    const x = x0 + (local % cols) * P.px + Math.floor((P.px - 14 * zoom) / 2), y = y0 + Math.floor(local / cols) * P.py + Math.floor((P.py - 15 * zoom) / 2);
    g.save(); g.translate(x, y); g.scale(zoom, zoom);
    drawSlot(game, g, 0, 0, b.slots[i], i === b.selected && i < HOTBAR, i === b.cursor);
    drawPickupCue(game, g, 0, 0, b.slots[i]);
    g.restore();
  }
  // THE LOADOUT (LS-239): what is worn, beside the bag; the hand box is the picked weapon
  { const gear = gearOf(game.state); const sel = b.slots[b.selected];
    for (const [slot, bx, by] of loadoutBoxes(!!game.touch)) {
      const st = slot === 'hand' ? (sel && ITEMS[sel.kind] && ITEMS[sel.kind].weapon ? sel : null) : gear[slot];
      drawSlot(game, g, bx, by, st, slot === 'hand');
      /* AN EMPTY WORN SLOT SHOWS A GHOST OF WHAT IT TAKES (the reference HUD renders, 2026-09-05: every empty gear
       * socket carried a faint glyph of its piece). Three empty black squares beside three words taught nothing;
       * a shadow of the hide, the treads and the bell says what goes there before you own one. */
      if (!st && slot !== 'hand') {
        const ghost = { body: 'hide', feet: 'treads', charm: 'bell' }[slot];
        if (ghost) { g.save(); g.globalAlpha = 0.55; drawWeapon(g, ghost, bx + 1, by + 1, false); g.restore(); }   // the UNLIT icon, over half: at 0.42 all three came out darker than the well they sit in and read as smudges (the art lens, round 50)
      }
      /* THE LABEL SITS ON A PLATE (the art lens, round 50). These three words were the only text in the game painted
       * onto live world: measured, seventeen distinct colours and a luma spread of 28 behind `charm` against eight
       * and 5.4 inside the panel, with the ink's contrast against the brightest thing behind it down to 1.34:1 --
       * grass and a rock showing through the letters. The boxes already have their own plate; the words did not. */
      /* THE LABEL IS CUT IN THE PANEL, small, under its own box. At the body font it was 24 pixels of word beside a
       * 14-pixel box, so on a desk it ran left into the grid and on a phone it sat over the cursor's caption; in the
       * three-by-five face it is 12, and it fits under the box it names with room either side. */
      const key = slot, kw = key.length * 4 - 1;
      if (slot !== 'hand' || game.touch) smallText(g, key, bx + 7 - Math.round(kw / 2), by + 18, BONE_INK, 20);   // on the plate's own bone below the well, so it is cut in ink
    } }
  if (b.lifted) { const local = b.cursor - P.offset; const x = x0 + (local % cols) * P.px + 6, y = y0 + Math.floor(local / cols) * P.py - 6; drawSlot(game, g, x, y, b.lifted, true); }
  /* ...AND THE BAG'S CAPTION STOPS AT THE EDGE TOO (the mobile lens, round 49). The hotbar's line was bounded in
   * round 48 and this one was not: a worn piece's caption measured 383 pixels from x 2 in a 256-pixel picture, so 129
   * of them were off the glass -- and the bag is the surface a phone player reads gear on. */
  const under = b.lifted || b.slots[b.cursor];
  /* THE CURSOR'S LINE GOES IN THE TITLE STRIP. It was printed on the dimmed world above the panel, the last text in
   * the bag still floating on the picture; the strip that says THE PACK has the width for it and is already bone. */
  { const cap = under ? stackCaption(under, true) + (stackWear(under, true) ? '  ' + stackWear(under, true) : '') : '';
    const capX = 60;
    smallText(g, cap, capX, HUD_H + 5, BONE_INK, 226 - capX - 6);
    bagEffectLines(under, !!game.touch).forEach((line, i) =>
      smallText(g, line, 8, HUD_H + 13 + i * 7, BONE_INK, game.touch ? 240 : 208));
  }   // desktop descriptions stop before the hand box; mobile stops above the worn row
  void rows;
  /* THE BOXES ARE DRAWN FOR EVERYONE NOW (2026-09-08; Vijay: "i want the entire game to be playable
   * without any instructions").
   *
   * A phone got two boxes it could hit and a desk got a line of text naming four keys -- "arrows  z
   * lift/drop  x drop  e close" -- which is the last instruction left in play and the plainest kind:
   * it tells you which button to press. The boxes were never a touch feature. `pointerdown` routes a
   * MOUSE through `game.tapAt` exactly as a finger, and `tapAt`'s bag branch has no touch gate, so
   * clicking where they are has always worked on a desk -- they simply were not painted, so nobody
   * could know. Drawing them is not new behaviour, it is the affordance made visible, which is what
   * the direction asks for: show it, do not say it. */
  {
    // two boxes a pointer can hit (see Game.tapAt): drop the held stack, close the bag
    for (const { x, y, w, h, action } of bagButtons(!!game.touch, game.cssScale)) {
      // the slot's own bevel, not a hairline rectangle (the mobile lens, round 41)
      const disabled = action === 'previous' && P.page === 0 || action === 'next' && P.page === P.pages - 1;
      g.fillStyle = UI.shadowHard; g.fillRect(x, y, w, h); g.fillStyle = disabled ? UI.hudLine : UI.gold; g.fillRect(x, y, w, 1); g.fillRect(x, y + h - 1, w, 1); g.fillStyle = UI.hudLine; g.fillRect(x, y, 1, h); g.fillRect(x + w - 1, y, 1, h);
      if (action === 'previous' || action === 'next') {
        g.fillStyle = disabled ? UI.hudLine : UI.paper;
        const dir = action === 'next' ? 1 : -1;
        for (let i = -4; i <= 4; i++) g.fillRect(x + Math.floor(w / 2) + dir * (4 - Math.abs(i)), y + Math.floor(h / 2) + i, 2, 1);
      } else game.text(action, Math.round(x + w / 2 - game.textWidth(action, 8) / 2), y + Math.floor((h - 8) / 2), UI.paper, 8);
    }
  }
}

/* THE INKED HUD (2026-09-28, LS-479; docs/ART.md 8, docs/art/concept/r3_4.jpg). The readout drawn the way the world
 * is: sepia ink and a watercolour wash on cream paper, at the glass's own resolution -- the display composites it over
 * the strip's rows (Display.setHudSource). Hearts with a red wash, nine inked slots holding the inked item icons, the
 * three beasts as medallions of their trophies, and a dial for the hour. Same places as the strip, so a click or a
 * touch on a slot lands where it always did. */
let inkIcons: HTMLImageElement | null = null, inkMeta: any = null, inkLoading = false;
function wantInkIcons() {
  if (inkIcons || inkLoading || typeof Image === 'undefined') return; inkLoading = true;
  fetch('ink/icons.json').then((r) => r.json()).then((m) => { inkMeta = m; const im = new Image(); im.onload = () => { inkIcons = im; }; im.src = 'ink/icons.png'; }).catch(() => { inkLoading = false; });
}
const INK = '#3b2a20', PAPER = '#efe6d2', WASH_RED = '#c8453a', WASH_OCHRE = '#d9b25a';
let hudCanvas: HTMLCanvasElement | null = null;
/** A hand's line: a path stroked twice, a hair apart, so it reads as ink and not as a vector. */
function inkStroke(g: CanvasRenderingContext2D, w: number, a = 0.9) { g.strokeStyle = INK; g.globalAlpha = a; g.lineWidth = w; g.stroke(); g.globalAlpha = a * 0.35; g.lineWidth = w * 1.8; g.stroke(); g.globalAlpha = 1; }
function inkIcon(g: CanvasRenderingContext2D, kind: string, x: number, y: number, size: number) {
  if (!inkIcons || !inkMeta || !inkMeta.icons[kind]) return false;
  const [ix, iy] = inkMeta.icons[kind], c = inkMeta.cell; g.drawImage(inkIcons, ix, iy, c, c, x, y, size, size); return true;
}
export function drawHudInk(game: any, K: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  wantInkIcons();
  if (!hudCanvas) hudCanvas = document.createElement('canvas');
  const W = Math.round(VW * K), H = Math.round(HUD_H * K);
  if (hudCanvas.width !== W || hudCanvas.height !== H) { hudCanvas.width = W; hudCanvas.height = H; }
  const bag = bagOf(game.state);
  /* DRAWN ONLY WHEN SOMETHING ON IT CHANGES (2026-09-28, perf3d on a 4x-throttled phone): the paths, gradients and icon
     draws cost a millisecond or two a frame redone sixty times a second for a strip that changes a few times a minute */
  { const sky = game.skyNow ? game.skyNow() : null, would = game.useWould ? !!game.useWould() : false;
    const open = (game.enemies || []).some((e: any) => e && !e.dead && e.def && e.def.beast && (e.state === 'spent' || e.state === 'wind' || e.reel > 0 || e.stun > 0 || e.flipped > 0 || e.bogged > 0));
    const key = [K, W, game.state.hp, game.state.maxHp, bag.selected, bag.slots.slice(0, HOTBAR).map((sl: any) => sl ? sl.kind + ':' + (sl.n || 1) + ':' + (sl.dur === undefined ? '' : Math.round(sl.dur)) : '').join(','),
      (game.state.beastsSlain || []).join(','), sky ? Math.round(sky.day * 96) + ':' + Math.round((sky.dark || 0) * 16) : '', would && Math.sin(game.frame / 7) > 0, open && Math.sin(game.frame / 4) > 0,
      !!(game.player && game.player.ailFlash && game.player.ailFlash.t > 0), game.player ? activeNames(game.player).join(',') : '', inkIcons ? 1 : 0,
      (() => { const last = game.needleBeast ? game.needleBeast() : null, lair = last && game.world && game.world.lairs ? game.world.lairs.find((l: any) => l.kind === last.kind) : null; return lair && game.player ? Math.round(Math.atan2(lair.py - game.player.cy, lair.px - game.player.cx) * 24) : ''; })()].join('|');   // the needle's bearing, to a fifteenth of a turn
    if ((drawHudInk as any).key === key) return hudCanvas; (drawHudInk as any).key = key; }
  const g = hudCanvas.getContext('2d')!; g.setTransform(K, 0, 0, K, 0, 0); g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
  // the paper, a faint wash pooled at the bottom, and a hand-ruled line under it all
  g.fillStyle = PAPER; g.fillRect(0, 0, VW, HUD_H);
  const gr = g.createLinearGradient(0, 0, 0, HUD_H); gr.addColorStop(0, 'rgba(217,178,90,0.0)'); gr.addColorStop(1, 'rgba(160,130,80,0.14)'); g.fillStyle = gr; g.fillRect(0, 0, VW, HUD_H);
  g.beginPath(); g.moveTo(1, HUD_H - 1.2); for (let x = 1; x <= VW - 1; x += 8) g.lineTo(x, HUD_H - 1.2 + Math.sin(x * 0.37) * 0.25); inkStroke(g, 0.55);
  // hearts: five, each a fifth of the vigor, washed red for what is left
  const LEAVES = 5, pips = Math.min(LEAVES, Math.max(1, Math.ceil(game.state.maxHp / 2))), per = game.state.maxHp / pips;
  const ail = !!(game.player && game.player.ailFlash && game.player.ailFlash.t > 0);
  const heart = (x: number, y: number, s: number) => { g.beginPath(); g.moveTo(x + s / 2, y + s * 0.92); g.bezierCurveTo(x - s * 0.1, y + s * 0.45, x + s * 0.05, y - s * 0.05, x + s / 2, y + s * 0.25); g.bezierCurveTo(x + s * 0.95, y - s * 0.05, x + s * 1.1, y + s * 0.45, x + s / 2, y + s * 0.92); g.closePath(); };
  for (let i = 0; i < pips; i++) {
    const x = 5 + i * 10, y = 7, s = 9, share = Math.max(0, Math.min(1, (game.state.hp - i * per) / per));
    heart(x, y, s); g.fillStyle = 'rgba(200,69,58,0.10)'; g.fill();
    if (share > 0) { g.save(); heart(x, y, s); g.clip(); g.fillStyle = ail ? '#e0783a' : WASH_RED; g.globalAlpha = 0.78; g.fillRect(x - 1, y - 1, (s + 2) * share, s + 2); g.globalAlpha = 0.3; g.fillStyle = '#fff4e0'; g.fillRect(x + 1.5, y + 1, 2, 2); g.restore(); }
    heart(x, y, s); inkStroke(g, 0.45);
  }
  { const names = game.player ? activeNames(game.player) : []; let x = 7;
    for (const n of names) { g.beginPath(); g.arc(x + 2, 19.5, 1.6, 0, Math.PI * 2); g.fillStyle = AIL_CHIP[n] || WASH_RED; g.fill(); inkStroke(g, 0.3); x += 6; if (x > 50) break; } }
  // the hotbar: nine inked slots, the picked one washed ochre and ruled heavier
  const would = game.useWould ? game.useWould() : null;
  const ANSWER: Record<string, string> = { sow: 'axe', oldshell: 'spear', bramblemother: 'torch' };
  const open = (game.enemies || []).find((e: any) => e && !e.dead && e.def && e.def.beast && (e.state === 'spent' || e.state === 'wind' || e.reel > 0 || e.stun > 0 || e.flipped > 0 || e.bogged > 0));
  const want = open ? ANSWER[open.type] : null;
  for (let i = 0; i < HOTBAR; i++) {
    const x = HOTBAR_X + i * HOTBAR_PITCH, y = HOTBAR_Y, sl = bag.slots[i], sel = bag.selected === i;
    g.beginPath(); g.roundRect(x - 0.5, y - 0.5, 15, 15, 1.6);
    g.fillStyle = sel ? 'rgba(217,178,90,0.42)' : 'rgba(160,140,100,0.10)'; g.fill(); inkStroke(g, sel ? 0.8 : 0.4, sel ? 1 : 0.7);
    if (sl && !inkIcon(g, sl.kind, x + 0.5, y + 0.5, 13)) { g.fillStyle = INK; g.font = '5px Georgia, serif'; g.fillText(sl.kind.slice(0, 4), x + 1, y + 9); }
    if (sl && sl.n > 1) { g.font = 'italic 5px Georgia, serif'; g.textAlign = 'right'; g.fillStyle = PAPER; g.fillText(String(sl.n), x + 14.2, y + 14.2); g.fillStyle = INK; g.fillText(String(sl.n), x + 13.8, y + 13.8); g.textAlign = 'left'; }
    const d = sl && ITEMS[sl.kind];
    if (sel && d && d.dur && sl.dur !== undefined) { const f = Math.max(0, Math.min(1, sl.dur / d.dur)); g.fillStyle = f > 0.3 ? WASH_OCHRE : WASH_RED; g.fillRect(x + 1, y + 15.6, 13 * f, 1.2); }
    const pulse = (sel && would && Math.sin(game.frame / 7) > 0) || (want && sl && sl.kind === want && Math.sin(game.frame / 4) > 0);
    if (pulse) { g.beginPath(); g.roundRect(x - 1.8, y - 1.8, 17.6, 17.6, 2.4); g.strokeStyle = WASH_OCHRE; g.lineWidth = 1.1; g.stroke(); }
  }
  // the three beasts: medallions of their trophies, washed gold when felled
  { const lairs = (game.world && game.world.lairs) || []; const slain = new Set(game.state.beastsSlain || []);
    const kinds: string[] = lairs.length ? lairs.map((l: any) => l.kind) : ['sow', 'oldshell', 'bramblemother'];
    const TROPHY: Record<string, string> = { sow: 'sowtusk', oldshell: 'greatshell', bramblemother: 'brambleheart' };
    /* THE NEEDLE IS ON THE BEAST IT POINTS AT (2026-09-29, the playthrough): it was a red dot on the rim of the clock, which
       said neither that it was a direction nor which beast it meant. The medallion of the beast last heard is ringed in
       red and carries the pointer on its own rim; the clock is only the hour. */
    const last = game.needleBeast ? game.needleBeast() : null, heardLair = last && lairs.find((l: any) => l.kind === last.kind);
    for (let i = 0; i < 3; i++) { const kind = kinds[i], cx = 207 + i * 12, cy = 12, down = slain.has(kind), heard = !down && !!heardLair && heardLair.kind === kind;
      g.beginPath(); g.arc(cx, cy, 5.4, 0, Math.PI * 2); g.fillStyle = down ? 'rgba(217,178,90,0.55)' : heard ? 'rgba(200,69,58,0.14)' : 'rgba(120,100,70,0.12)'; g.fill();
      g.save(); g.globalAlpha = down || heard ? 1 : 0.45; inkIcon(g, TROPHY[kind] || 'horn', cx - 4.6, cy - 4.6, 9.2); g.restore();
      g.beginPath(); g.arc(cx, cy, 5.4, 0, Math.PI * 2); if (heard) { g.strokeStyle = WASH_RED; g.lineWidth = 0.9; g.stroke(); } else inkStroke(g, 0.45);
      if (heard && game.player) { const a = Math.atan2(heardLair.py - game.player.cy, heardLair.px - game.player.cx), c = Math.cos(a), sn = Math.sin(a);
        g.beginPath(); g.moveTo(cx + c * 8.2, cy + sn * 8.2); g.lineTo(cx + c * 5.6 - sn * 1.9, cy + sn * 5.6 + c * 1.9); g.lineTo(cx + c * 5.6 + sn * 1.9, cy + sn * 5.6 - c * 1.9); g.closePath(); g.fillStyle = WASH_RED; g.fill(); } } }
  // the hour: a dial washed by the sky, the day's hand, and the needle toward the beast last heard
  { const cx = 246, cy = 12, r = 7.5, sky = game.skyNow ? game.skyNow() : null, dark = Math.max(0, Math.min(1, (sky ? sky.dark : 0) || 0));
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fillStyle = mixHex('#f3e7c4', '#3a4a78', dark); g.fill();
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); inkStroke(g, 0.55);
    for (let k = 0; k < 12; k++) { const a = k / 12 * Math.PI * 2; g.beginPath(); g.moveTo(cx + Math.sin(a) * r * 0.78, cy - Math.cos(a) * r * 0.78); g.lineTo(cx + Math.sin(a) * r * 0.92, cy - Math.cos(a) * r * 0.92); inkStroke(g, 0.3, 0.7); }
    const a = (sky ? sky.day : 0) * Math.PI * 2; g.beginPath(); g.moveTo(cx, cy); g.lineTo(cx + Math.sin(a) * r * 0.66, cy - Math.cos(a) * r * 0.66); inkStroke(g, 0.6);
  }
  return hudCanvas;
}
